// features/automation/config/automationConstants.js

/**
 * Display constants and configuration for the Automation screen.
 * All mock/seed data has been removed — real data comes from the Zustand store
 * (hydrated from S3 request_payload.json).
 */

// Sentinel value for a request/response body field that should be omitted
// from the generated body for this permutation. When a field's value equals
// this string, the body editor renders it as an "excluded" icon rather than
// editable text, and the value is preserved verbatim (not type-coerced) so
// the test engine can recognise it.
export const EXCLUDE_VALUE = 'exclude for this permutation';

// HTTP method display config (color + label) — standard REST colors
export const HTTP_METHOD_CONFIG = {
  GET: { label: 'GET', color: '#22c55e' },
  POST: { label: 'POST', color: '#f59e0b' },
  PUT: { label: 'PUT', color: '#3b82f6' },
  PATCH: { label: 'PATCH', color: '#8b5cf6' },
  DELETE: { label: 'DELETE', color: '#ef4444' },
  HEAD: { label: 'HEAD', color: '#06b6d4' },
  OPTIONS: { label: 'OPTIONS', color: '#6b7280' },
};

// Common HTTP headers for quick-add dropdown
export const COMMON_HEADERS = [
  { name: 'Content-Type', value: 'application/json', description: 'Request body format' },
  { name: 'Accept', value: 'application/json', description: 'Expected response format' },
  { name: 'Authorization', value: 'Bearer {{token}}', description: 'Authentication token' },
  { name: 'X-API-Key', value: '', description: 'API key authentication' },
  { name: 'Connection', value: 'keep-alive', description: 'Connection status' },
  { name: 'Accept-Encoding', value: 'gzip, deflate, br', description: 'Supported compression' },
];

// Step type display config (color + label + badge)
export const STEP_TYPE_CONFIG = {
  Api: { label: 'API', color: '#3b82f6', badge: 'API' },
  Database: { label: 'Database', color: '#d97706', badge: 'Database' },
  'Web-UI': { label: 'Web UI', color: '#22c55e', badge: 'Web UI' },
  Mobile: { label: 'Mobile', color: '#8b5cf6', badge: 'Mobile' },
};

// The backend persists stepType lowercase (e.g. "mobile"), but the UI keys off the
// canonical casing in STEP_TYPE_CONFIG ("Mobile"). Anywhere we read a step straight
// from a stored payload (e.g. the reuse pickers) must normalize before the UI uses it,
// otherwise the step type goes unrecognized and no editor renders.
export const normalizeStepType = (t) => {
  if (!t) return t;
  const map = { mobile: 'Mobile', 'web-ui': 'Web-UI', api: 'Api', database: 'Database' };
  return map[t.toLowerCase()] ?? t;
};

// Variable value types
export const VARIABLE_VALUE_TYPES = {
  NORMAL: 'normal',
  SENSITIVE: 'sensitive',
  PERMANENTLY_SENSITIVE: 'permanently_sensitive',
};

// Default test behaviour settings
export const DEFAULT_TEST_SETTINGS = {
  stopOnFailure: false,
};

// ===== Web UI Step Constants =====

// Browser choices for a Web-UI step. `value` is the backend enum sent as
// `browserType` in the payload; `label` is what the user sees. These MUST stay the
// enum values — the dropdown matches the stored value against them to pick a label,
// so a mismatch leaves the control blank.
// Only GOOGLE_CHROME is implemented today; the rest are disabled placeholders and
// their enum names need confirming with the backend before being enabled.
export const WEB_UI_BROWSER_TYPES = [
  { value: 'GOOGLE_CHROME',   label: 'Chrome',  enabled: true },
  { value: 'MOZILLA_FIREFOX', label: 'Firefox', enabled: false },
  { value: 'MICROSOFT_EDGE',  label: 'Edge',    enabled: false },
  { value: 'SAFARI',          label: 'Safari',  enabled: false }
];

export const DEFAULT_WEB_UI_BROWSER_TYPE = 'GOOGLE_CHROME';

// Older scripts were saved with the lowercase UI values ('chrome', 'firefox', …)
// because the dropdown used to emit them. Map those onto the backend enum so the
// label resolves and the next save writes the correct value.
const LEGACY_BROWSER_TYPES = {
  chrome: 'GOOGLE_CHROME',
  firefox: 'MOZILLA_FIREFOX',
  edge: 'MICROSOFT_EDGE',
  safari: 'SAFARI',
};

/**
 * Resolve any stored browserType to the backend enum.
 * Unknown values are returned unchanged so nothing is silently rewritten.
 */
export const normalizeWebUiBrowserType = (value) => {
  if (!value) return DEFAULT_WEB_UI_BROWSER_TYPE;
  return LEGACY_BROWSER_TYPES[String(value).toLowerCase()] || value;
};

// Element identifier strategies
export const WEB_UI_IDENTIFIER_TYPES = [
  { value: 'css',             label: 'CSS Selector',      hint: '#id, .class, tag' },
  { value: 'id',              label: 'Element ID',         hint: 'Unique id attribute' },
  { value: 'xpath',           label: 'XPath',              hint: '//tag[@attr="val"]' },
  { value: 'name',            label: 'Name Attribute',     hint: 'name="fieldName"' },
  { value: 'class',           label: 'Class Name',         hint: 'CSS class on the element' },
  { value: 'tagName',         label: 'HTML Tag',           hint: 'div, input, button, etc.' },
  { value: 'linkText',        label: 'Link Text (exact)',  hint: 'Full text of a link' },
  { value: 'partialLinkText', label: 'Link Text (partial)',hint: 'Part of a link\'s text' },
];

// Action categories with display info (17 categories)
export const WEB_UI_ACTION_CATEGORIES = {
  NAVIGATION:      { label: 'Pages & URLs',            color: '#22c55e' },
  CLICK_MOUSE:     { label: 'Clicks & Mouse',          color: '#3b82f6' },
  INPUT_KEYBOARD:  { label: 'Typing & Keys',           color: '#f59e0b' },
  SELECTION:       { label: 'Dropdowns & Checkboxes',   color: '#8b5cf6' },
  DATA_EXTRACTION: { label: 'Read from Page',          color: '#06b6d4' },
  VERIFICATIONS:   { label: 'Quick Checks',            color: '#10b981' },
  HARD_ASSERT:     { label: 'Hard Assertions',         color: '#ef4444' },
  SOFT_ASSERT:     { label: 'Soft Assertions',         color: '#f97316' },
  WAIT_OPS:        { label: 'Wait & Pause',            color: '#6366f1' },
  TABLE_OPS:       { label: 'Tables',                  color: '#ec4899' },
  ALERT_POPUP:     { label: 'Alerts & Popups',         color: '#d97706' },
  FILE_OPS:        { label: 'File Uploads',             color: '#14b8a6' },
  DATA_MATH:       { label: 'Data & Math',              color: '#0ea5e9' },
  SCROLLING:       { label: 'Scrolling',               color: '#a855f7' },
  FRAME_WINDOW:    { label: 'Tabs & Frames',           color: '#0ea5e9' },
  ADVANCED:        { label: 'Advanced',                color: '#64748b' },
  CONTROL_FLOW:    { label: 'Control Flow',            color: '#f59e0b' },
  // Break Loop / End Permutation. Deliberately NOT in CONTROL_FLOW: nested action
  // pickers hide that whole category (control flow is one level deep only), but
  // these two are most useful *inside* a loop or branch body.
  FLOW_SIGNAL:     { label: 'Stop & Exit',             color: '#e11d48' },
};

// All 104 Web UI actions — each defines category, label, and which fields to show
export const WEB_UI_ACTIONS = {
  // --- Navigation (4) ---
  'Open URL':      { category: 'NAVIGATION', label: 'Open URL',      requiredFields: ['locator'], optionalFields: ['description', 'timeout'] },
  'Go Back':       { category: 'NAVIGATION', label: 'Go Back',       requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Go Forward':    { category: 'NAVIGATION', label: 'Go Forward',    requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Refresh Page':  { category: 'NAVIGATION', label: 'Refresh Page',  requiredFields: [], optionalFields: ['description', 'timeout'] },

  // --- Click & Mouse (9) ---
  'Click Element':           { category: 'CLICK_MOUSE', label: 'Click Element',           requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Double Click Element':    { category: 'CLICK_MOUSE', label: 'Double Click Element',    requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Right Click Element':     { category: 'CLICK_MOUSE', label: 'Right Click Element',     requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Click and Hold Element':  { category: 'CLICK_MOUSE', label: 'Click and Hold Element',  requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Release Mouse Button':    { category: 'CLICK_MOUSE', label: 'Release Mouse Button',    requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Hover Over Element':      { category: 'CLICK_MOUSE', label: 'Hover Over Element',      requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Move Mouse to Element':   { category: 'CLICK_MOUSE', label: 'Move Mouse to Element',   requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Drag and Drop Element':   { category: 'CLICK_MOUSE', label: 'Drag and Drop Element',   requiredFields: ['identifierType', 'locator', 'targetValue'], optionalFields: ['description', 'timeout'] },
  'Drag and Drop by Offset': { category: 'CLICK_MOUSE', label: 'Drag and Drop by Offset', requiredFields: ['identifierType', 'locator', 'xOffset', 'yOffset'], optionalFields: ['description', 'timeout'] },

  // --- Input & Keyboard (6) ---
  'Type Text into Field':  { category: 'INPUT_KEYBOARD', label: 'Type Text into Field',  requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Send Keyboard Keys':    { category: 'INPUT_KEYBOARD', label: 'Send Keyboard Keys',    requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Clear Input Field':     { category: 'INPUT_KEYBOARD', label: 'Clear Input Field',     requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Press Key Down':        { category: 'INPUT_KEYBOARD', label: 'Press Key Down',        requiredFields: ['expectedValue'], optionalFields: ['description', 'timeout'] },
  'Release Key Up':        { category: 'INPUT_KEYBOARD', label: 'Release Key Up',        requiredFields: ['expectedValue'], optionalFields: ['description', 'timeout'] },
  'Press Keyboard Key':    { category: 'INPUT_KEYBOARD', label: 'Press Keyboard Key',    requiredFields: ['expectedValue'], optionalFields: ['description', 'timeout'] },

  // --- Selection & Checkboxes (7) ---
  'Select from Dropdown':     { category: 'SELECTION', label: 'Select from Dropdown',     requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Select by Position Number':{ category: 'SELECTION', label: 'Select by Position Number',requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Select by Value':          { category: 'SELECTION', label: 'Select by Value',          requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Select by Display Text':   { category: 'SELECTION', label: 'Select by Display Text',   requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Check Checkbox':           { category: 'SELECTION', label: 'Check Checkbox',           requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Uncheck Checkbox':         { category: 'SELECTION', label: 'Uncheck Checkbox',         requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Toggle Checkbox':          { category: 'SELECTION', label: 'Toggle Checkbox',          requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },

  // --- Data Extraction (6) ---
  'Extract Value from Element': { category: 'DATA_EXTRACTION', label: 'Extract Value from Element', requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Text from Element':      { category: 'DATA_EXTRACTION', label: 'Get Text from Element',      requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Element Attribute':      { category: 'DATA_EXTRACTION', label: 'Get Element Attribute',      requiredFields: ['identifierType', 'locator', 'expectedValue', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get CSS Property Value':     { category: 'DATA_EXTRACTION', label: 'Get CSS Property Value',     requiredFields: ['identifierType', 'locator', 'expectedValue', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Page Title':             { category: 'DATA_EXTRACTION', label: 'Get Page Title',             requiredFields: ['variableName'], optionalFields: ['description', 'timeout'] },
  'Get Current URL':            { category: 'DATA_EXTRACTION', label: 'Get Current URL',            requiredFields: ['variableName'], optionalFields: ['description', 'timeout'] },

  // --- Verifications (9) ---
  'Verify Element Exists':         { category: 'VERIFICATIONS', label: 'Verify Element Exists',         requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Element Does Not Exist': { category: 'VERIFICATIONS', label: 'Verify Element Does Not Exist', requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Element is Visible':  { category: 'VERIFICATIONS', label: 'Verify Element is Visible',  requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Element is Enabled':  { category: 'VERIFICATIONS', label: 'Verify Element is Enabled',  requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Element is Disabled': { category: 'VERIFICATIONS', label: 'Verify Element is Disabled', requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Text Content':        { category: 'VERIFICATIONS', label: 'Verify Text Content',        requiredFields: ['identifierType', 'locator', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Verify Text Contains':       { category: 'VERIFICATIONS', label: 'Verify Text Contains',       requiredFields: ['identifierType', 'locator', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Verify Element is Selected': { category: 'VERIFICATIONS', label: 'Verify Element is Selected', requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Element Attribute':   { category: 'VERIFICATIONS', label: 'Verify Element Attribute',   requiredFields: ['identifierType', 'locator', 'expectedValue'], optionalFields: ['description', 'timeout'] },

  // --- Hard Assertions — stop on failure (10) ---
  'Hard Assert Equals':               { category: 'HARD_ASSERT', label: 'Hard Assert Equals',               requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Hard Assert Not Equals':           { category: 'HARD_ASSERT', label: 'Hard Assert Not Equals',           requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Hard Assert True':                 { category: 'HARD_ASSERT', label: 'Hard Assert True',                 requiredFields: ['actualValue'], optionalFields: ['description', 'timeout'] },
  'Hard Assert False':                { category: 'HARD_ASSERT', label: 'Hard Assert False',                requiredFields: ['actualValue'], optionalFields: ['description', 'timeout'] },
  'Hard Assert Null':                 { category: 'HARD_ASSERT', label: 'Hard Assert Null',                 requiredFields: ['actualValue'], optionalFields: ['description', 'timeout'] },
  'Hard Assert Not Null':             { category: 'HARD_ASSERT', label: 'Hard Assert Not Null',             requiredFields: ['actualValue'], optionalFields: ['description', 'timeout'] },
  'Hard Assert Contains':             { category: 'HARD_ASSERT', label: 'Hard Assert Contains',             requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Hard Assert Not Contains':         { category: 'HARD_ASSERT', label: 'Hard Assert Not Contains',         requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Hard Assert Contains Ignore Case': { category: 'HARD_ASSERT', label: 'Hard Assert Contains Ignore Case', requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Hard Assert Matches Regex':        { category: 'HARD_ASSERT', label: 'Hard Assert Matches Regex',        requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },

  // --- Soft Assertions — continue on failure (10) ---
  'Soft Assert Equals':               { category: 'SOFT_ASSERT', label: 'Soft Assert Equals',               requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Soft Assert Not Equals':           { category: 'SOFT_ASSERT', label: 'Soft Assert Not Equals',           requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Soft Assert True':                 { category: 'SOFT_ASSERT', label: 'Soft Assert True',                 requiredFields: ['actualValue'], optionalFields: ['description', 'timeout'] },
  'Soft Assert False':                { category: 'SOFT_ASSERT', label: 'Soft Assert False',                requiredFields: ['actualValue'], optionalFields: ['description', 'timeout'] },
  'Soft Assert Null':                 { category: 'SOFT_ASSERT', label: 'Soft Assert Null',                 requiredFields: ['actualValue'], optionalFields: ['description', 'timeout'] },
  'Soft Assert Not Null':             { category: 'SOFT_ASSERT', label: 'Soft Assert Not Null',             requiredFields: ['actualValue'], optionalFields: ['description', 'timeout'] },
  'Soft Assert Contains':             { category: 'SOFT_ASSERT', label: 'Soft Assert Contains',             requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Soft Assert Not Contains':         { category: 'SOFT_ASSERT', label: 'Soft Assert Not Contains',         requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Soft Assert Contains Ignore Case': { category: 'SOFT_ASSERT', label: 'Soft Assert Contains Ignore Case', requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Soft Assert Matches Regex':        { category: 'SOFT_ASSERT', label: 'Soft Assert Matches Regex',        requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description', 'timeout'] },

  // --- Wait Operations (9) ---
  'Wait':                             { category: 'WAIT_OPS', label: 'Wait',                             requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Wait for Element to Appear':       { category: 'WAIT_OPS', label: 'Wait for Element to Appear',       requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Element to be Visible':   { category: 'WAIT_OPS', label: 'Wait for Element to be Visible',   requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Element to be Enabled':   { category: 'WAIT_OPS', label: 'Wait for Element to be Enabled',   requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Element to be Disabled':  { category: 'WAIT_OPS', label: 'Wait for Element to be Disabled',  requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Element to be Clickable': { category: 'WAIT_OPS', label: 'Wait for Element to be Clickable', requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Loading to Complete':     { category: 'WAIT_OPS', label: 'Wait for Loading to Complete',     requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Loader Cycle':            { category: 'WAIT_OPS', label: 'Wait for Loader Cycle',            requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Element (No Loader)':     { category: 'WAIT_OPS', label: 'Wait for Element (No Loader)',     requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for All Loaders to Disappear':{ category: 'WAIT_OPS', label: 'Wait for All Loaders to Disappear',requiredFields: [], optionalFields: ['description', 'timeout'] },

  // --- Table Operations (21) ---
  'Check if Table Contains Value':    { category: 'TABLE_OPS', label: 'Check if Table Contains Value',    requiredFields: ['identifierType', 'locator', 'searchValue'], optionalFields: ['description', 'timeout'] },
  'Click Element in Table Cell':      { category: 'TABLE_OPS', label: 'Click Element in Table Cell',      requiredFields: ['identifierType', 'locator', 'searchColumnName', 'columnName', 'elementType'], optionalFields: ['columnValue', 'description', 'timeout'] },
  'Click Table Header':               { category: 'TABLE_OPS', label: 'Click Table Header',               requiredFields: ['identifierType', 'locator', 'columnName'], optionalFields: ['description', 'timeout'] },
  'Filter Table':                     { category: 'TABLE_OPS', label: 'Filter Table',                     requiredFields: ['identifierType', 'locator', 'filterValue'], optionalFields: ['description', 'timeout'] },
  'Find All Matching Rows in Table':  { category: 'TABLE_OPS', label: 'Find All Matching Rows in Table',  requiredFields: ['identifierType', 'locator', 'columnName', 'variableName'], optionalFields: ['columnValue', 'description', 'timeout'] },
  'Find Table Row by Text':           { category: 'TABLE_OPS', label: 'Find Table Row by Text',           requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Navigate Table Pagination':        { category: 'TABLE_OPS', label: 'Navigate Table Pagination',        requiredFields: ['identifierType', 'locator', 'paginationAction'], optionalFields: ['description', 'timeout'] },
  'Extract Value from Table':         { category: 'TABLE_OPS', label: 'Extract Value from Table',         requiredFields: ['identifierType', 'locator', 'searchColumnName', 'columnName', 'variableName'], optionalFields: ['columnValue', 'description', 'timeout'] },
  'Get All Table Data':               { category: 'TABLE_OPS', label: 'Get All Table Data',               requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Table Cell Data':              { category: 'TABLE_OPS', label: 'Get Table Cell Data',              requiredFields: ['identifierType', 'locator', 'searchColumnName', 'columnName'], optionalFields: ['columnValue', 'description', 'variableName', 'timeout'] },
  'Get Table Cell Element':           { category: 'TABLE_OPS', label: 'Get Table Cell Element',           requiredFields: ['identifierType', 'locator', 'searchColumnName', 'columnName'], optionalFields: ['columnValue', 'description', 'variableName', 'timeout'] },
  'Get Table Column Count':           { category: 'TABLE_OPS', label: 'Get Table Column Count',           requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Table Column Data':            { category: 'TABLE_OPS', label: 'Get Table Column Data',            requiredFields: ['identifierType', 'locator', 'columnName', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Table Headers':                { category: 'TABLE_OPS', label: 'Get Table Headers',                requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Table Row by Column Value':    { category: 'TABLE_OPS', label: 'Get Table Row by Column Value',    requiredFields: ['identifierType', 'locator', 'columnName', 'variableName'], optionalFields: ['columnValue', 'description', 'timeout'] },
  'Get Table Row Count':              { category: 'TABLE_OPS', label: 'Get Table Row Count',              requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Table Row Data':               { category: 'TABLE_OPS', label: 'Get Table Row Data',               requiredFields: ['identifierType', 'locator'], optionalFields: ['targetValue', 'description', 'variableName', 'timeout'] },
  'Select Table Checkbox':            { category: 'TABLE_OPS', label: 'Select Table Checkbox',            requiredFields: ['identifierType', 'locator'], optionalFields: ['targetValue', 'description', 'timeout'] },
  'Select from Table Dropdown':       { category: 'TABLE_OPS', label: 'Select from Table Dropdown',       requiredFields: ['identifierType', 'locator', 'additionalValue', 'textToEnter'], optionalFields: ['targetValue', 'description', 'timeout'] },
  'Verify Table Sort Order':          { category: 'TABLE_OPS', label: 'Verify Table Sort Order',          requiredFields: ['identifierType', 'locator', 'columnName', 'sortOrder', 'dataType'], optionalFields: ['description', 'timeout'] },

  // --- Alert & Popup (4) ---
  'Accept Alert/Popup':  { category: 'ALERT_POPUP', label: 'Accept Alert/Popup',  requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Dismiss/Cancel Alert':{ category: 'ALERT_POPUP', label: 'Dismiss/Cancel Alert',requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Get Alert Text':      { category: 'ALERT_POPUP', label: 'Get Alert Text',      requiredFields: ['variableName'], optionalFields: ['description', 'timeout'] },
  'Type into Alert':     { category: 'ALERT_POPUP', label: 'Type into Alert',     requiredFields: ['expectedValue'], optionalFields: ['description', 'timeout'] },

  // --- File Operations (2) ---
  'Upload a File':         { category: 'FILE_OPS', label: 'Upload a File',         requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Upload Multiple Files': { category: 'FILE_OPS', label: 'Upload Multiple Files', requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },

  // --- Scrolling (4) ---
  'Scroll to Element':       { category: 'SCROLLING', label: 'Scroll to Element',       requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Scroll Page Up':          { category: 'SCROLLING', label: 'Scroll Page Up',          requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Scroll Page Down':        { category: 'SCROLLING', label: 'Scroll Page Down',        requiredFields: [], optionalFields: ['description', 'timeout'] },

  // --- Frame & Window (5) ---
  'Switch to iFrame':            { category: 'FRAME_WINDOW', label: 'Switch to iFrame',            requiredFields: ['locator'], optionalFields: ['description', 'identifierType', 'timeout'] },
  'Switch to Window/Tab':        { category: 'FRAME_WINDOW', label: 'Switch to Window/Tab',        requiredFields: [], optionalFields: ['expectedValue', 'description', 'timeout'] },
  'Switch to Main Content':      { category: 'FRAME_WINDOW', label: 'Switch to Main Content',      requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Switch to Parent Frame':      { category: 'FRAME_WINDOW', label: 'Switch to Parent Frame',      requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Close Current Window/Tab':    { category: 'FRAME_WINDOW', label: 'Close Current Window/Tab',    requiredFields: [], optionalFields: ['description', 'timeout'] },

  // --- Advanced (8) ---
  'Execute JavaScript':       { category: 'ADVANCED', label: 'Execute JavaScript',       requiredFields: ['expectedValue'], optionalFields: ['description', 'variableName', 'timeout'] },
  'Take Screenshot':          { category: 'ADVANCED', label: 'Take Screenshot',          requiredFields: [], optionalFields: ['description', 'variableName', 'timeout'] },
  'Focus on Element':         { category: 'ADVANCED', label: 'Focus on Element',         requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Remove Focus from Element':{ category: 'ADVANCED', label: 'Remove Focus from Element',requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Submit Form':              { category: 'ADVANCED', label: 'Submit Form',              requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Maximize Browser Window':  { category: 'ADVANCED', label: 'Maximize Browser Window',  requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Set Window Size':          { category: 'ADVANCED', label: 'Set Window Size',          requiredFields: ['expectedValue'], optionalFields: ['description', 'timeout'] },

  // --- Control Flow (3) ---
  'Conditional': { category: 'CONTROL_FLOW', label: 'Conditional',      requiredFields: [], optionalFields: ['description'] },
  'Loop':        { category: 'CONTROL_FLOW', label: 'Loop',             requiredFields: [], optionalFields: ['description'] },
  'ForEach':     { category: 'CONTROL_FLOW', label: 'For Each Element', requiredFields: [], optionalFields: ['description'] },

  // --- Stop & Exit (2) ---
  // Both carry an optional `condition` object instead of the flat field list, so
  // they declare no fields here — FlowSignalFields renders their editor.
  'Break Loop':     { category: 'FLOW_SIGNAL', label: 'Break Loop',     requiredFields: [], optionalFields: ['description'] },
  'End Permutation':  { category: 'FLOW_SIGNAL', label: 'End Permutation',  requiredFields: [], optionalFields: ['description'] },

  // --- Data & Math (3) ---
  'Apply Regex':     { category: 'DATA_MATH', label: 'Apply Regex',     requiredFields: ['textToEnter', 'expectedValue', 'variableName'], optionalFields: ['description'] },
  'Parse to Number': { category: 'DATA_MATH', label: 'Parse to Number', requiredFields: ['textToEnter', 'variableName'],                  optionalFields: ['description'] },
  'Calculate':       { category: 'DATA_MATH', label: 'Calculate',       requiredFields: ['expectedValue', 'variableName'],                optionalFields: ['description'] },
};

// Field labels for dynamic rendering
export const WEB_UI_FIELD_LABELS = {
  identifierType:   'Find By',
  locator:          'Target Element',
  textToEnter:      'Text to Type',
  expectedValue:    'Expected Value',
  actualValue:      'Actual Value',
  targetValue:      'Target Value',
  variableName:     'Save As Variable',
  timeout:          'Wait Up To',
  additionalValue:  'Additional Value',
  searchColumnName: 'Column to Search',
  searchValue:      'Value to Find',
  columnName:       'Column Name',
  columnValue:      'Column Value',
  elementType:      'Element Type',
  filterValue:      'Filter Value',
  paginationAction: 'Page Action',
  sortOrder:        'Sort Order',
  dataType:         'Data Type',
  xOffset:          'Move Right/Left (px)',
  yOffset:          'Move Up/Down (px)',
};

// Placeholder examples shown inside each field input
export const WEB_UI_FIELD_PLACEHOLDERS = {
  locator:          'e.g. #login-btn  or  //button[@id="submit"]',
  textToEnter:      'e.g. Hello World',
  expectedValue:    'e.g. Welcome back',
  actualValue:      'e.g. variable name or value',
  targetValue:      'e.g. #drop-zone',
  variableName:     'e.g. myVariable',
  additionalValue:  'e.g. #drop-zone',
  searchColumnName: 'e.g. Email',
  searchValue:      'e.g. john@example.com',
  columnName:       'e.g. Status',
  columnValue:      'e.g. john@example.com',
  elementType:      'e.g. button, a, input',
  filterValue:      'e.g. Active',
  paginationAction: 'e.g. next, previous, 3',
  xOffset:          '0',
  yOffset:          '0',
};

// Short helper text shown below the field label
export const WEB_UI_FIELD_HINTS = {
  identifierType:   'How to find the element on the page',
  locator:          'The selector or path that points to the element',
  textToEnter:      'The text or keys to send',
  expectedValue:    'What the result should be',
  actualValue:      'The actual value to compare (can be a variable name)',
  targetValue:      'The target value for this action',
  variableName:     'Reuse this value in later steps with {{variableName}}',
  timeout:          'Max time to wait in milliseconds (leave empty for default)',
  additionalValue:  'Additional value for this action',
  searchColumnName: 'Which column to look in',
  searchValue:      'What to search for in that column',
  columnName:       'The name of the column in the table header',
  columnValue:      'The value to match in the table column',
  elementType:      'The HTML tag to look for (e.g. button, a, input)',
  filterValue:      'The value to filter the table by',
  paginationAction: 'Which page to go to',
  sortOrder:        'Expected sort direction',
  dataType:         'How values should be compared',
  xOffset:          'Pixels to move horizontally (positive = right)',
  yOffset:          'Pixels to move vertically (positive = down)',
};

// Per-action overrides for field labels, hints, and placeholders.
// These make generic field names (like "locator" or "textToEnter")
// more meaningful in the context of a specific action.
export const WEB_UI_FIELD_OVERRIDES = {
  // --- Navigation ---
  'Open URL': {
    locator: {
      label: 'Page URL',
      hint: 'The web address to navigate to',
      placeholder: 'e.g. https://example.com/login  or  /dashboard',
    },
  },

  // --- Input & Keyboard ---
  'Type Text into Field': {
    textToEnter: { label: 'Text to Type', hint: 'The text that will be entered into the field', placeholder: 'e.g. testuser@example.com' },
  },
  'Send Keyboard Keys': {
    textToEnter: {
      label: 'Key to Send',
      hint: 'Select the key to send to this element',
      placeholder: '',
    },
  },
  'Press Key Down': {
    expectedValue: { label: 'Key to Hold', hint: 'The key to press and hold down', placeholder: 'e.g. SHIFT, CONTROL, ALT' },
  },
  'Release Key Up': {
    expectedValue: { label: 'Key to Release', hint: 'The key to let go of', placeholder: 'e.g. SHIFT, CONTROL, ALT' },
  },
  'Press Keyboard Key': {
    expectedValue: { label: 'Key to Press', hint: 'A single key to press and release', placeholder: 'e.g. ENTER, ESCAPE, F5' },
  },

  // --- Selection ---
  'Select from Dropdown': {
    textToEnter: { label: 'Option to Select', hint: 'Auto-detects by index, value, or visible text', placeholder: 'e.g. United States' },
  },
  'Select by Position Number': {
    textToEnter: { label: 'Position (0-based)', hint: 'The index of the option, starting from 0', placeholder: 'e.g. 0, 1, 2' },
  },
  'Select by Value': {
    textToEnter: { label: 'Option Value', hint: 'The value="" attribute of the <option> tag', placeholder: 'e.g. us, uk, credit_card' },
  },
  'Select by Display Text': {
    textToEnter: { label: 'Display Text', hint: 'The exact visible text shown in the dropdown', placeholder: 'e.g. English (US)' },
  },

  // --- Drag & Drop ---
  'Drag and Drop Element': {
    targetValue: { label: 'Drop Onto', hint: 'The target element selector to drop onto', placeholder: 'e.g. #drop-zone' },
  },
  'Drag and Drop by Offset': {
    xOffset: { label: 'Horizontal (px)', hint: 'Pixels to drag right (negative = left)', placeholder: 'e.g. 100' },
    yOffset: { label: 'Vertical (px)', hint: 'Pixels to drag down (negative = up)', placeholder: 'e.g. 0' },
  },

  // --- Data Extraction ---
  'Extract Value from Element': {
    variableName: { label: 'Save As', hint: 'Name to reuse this value later with {{name}}', placeholder: 'e.g. orderId, userName' },
  },
  'Get Text from Element': {
    variableName: { label: 'Save As', hint: 'Name to reuse the extracted text later', placeholder: 'e.g. welcomeMessage' },
  },
  'Get Element Attribute': {
    expectedValue: { label: 'Attribute Name', hint: 'Which HTML attribute to read from the element', placeholder: 'e.g. href, class, data-id, placeholder' },
    variableName: { label: 'Save As', hint: 'Name to reuse this attribute value later', placeholder: 'e.g. downloadUrl' },
  },
  'Get CSS Property Value': {
    expectedValue: { label: 'CSS Property', hint: 'Which CSS style to read from the element', placeholder: 'e.g. color, background-color, display' },
    variableName: { label: 'Save As', hint: 'Name to reuse this CSS value later', placeholder: 'e.g. statusColor' },
  },
  'Get Page Title': {
    variableName: { label: 'Save As', hint: 'Name to reuse the page title later', placeholder: 'e.g. pageTitle' },
  },
  'Get Current URL': {
    variableName: { label: 'Save As', hint: 'Name to reuse the current URL later', placeholder: 'e.g. currentUrl' },
  },

  // --- Verifications ---
  'Verify Text Content': {
    expectedValue: { label: 'Expected Text', hint: 'The text the element should contain', placeholder: 'e.g. Welcome to Dashboard' },
  },
  'Verify Element Attribute': {
    expectedValue: { label: 'Expected Attribute Value', hint: 'Attribute name and value separated by colon (name:value)', placeholder: 'e.g. type:text, placeholder:Enter your email' },
  },
  'Verify Text Contains': {
    expectedValue: { label: 'Expected Substring', hint: 'Passes when the element\'s text contains this anywhere', placeholder: 'e.g. logged in' },
  },

  // --- Hard Assertions ---
  'Hard Assert Equals': {
    expectedValue: { label: 'Expected Value', hint: 'The value you expect', placeholder: 'e.g. john.doe' },
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to compare', placeholder: 'e.g. username' },
  },
  'Hard Assert Not Equals': {
    expectedValue: { label: 'Not Expected Value', hint: 'The value it should NOT be', placeholder: 'e.g. empty string or error' },
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to compare', placeholder: 'e.g. errorMsg' },
  },
  'Hard Assert True': {
    actualValue: { label: 'Value to Check', hint: 'Should resolve to "true"', placeholder: 'e.g. isLoggedIn' },
  },
  'Hard Assert False': {
    actualValue: { label: 'Value to Check', hint: 'Should resolve to "false"', placeholder: 'e.g. isAdmin' },
  },
  'Hard Assert Null': {
    actualValue: { label: 'Variable to Check', hint: 'Should be null or empty', placeholder: 'e.g. errorCode' },
  },
  'Hard Assert Not Null': {
    actualValue: { label: 'Variable to Check', hint: 'Should NOT be null or empty', placeholder: 'e.g. userId' },
  },
  'Hard Assert Contains': {
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to check', placeholder: 'e.g. {{step1.message}}' },
    expectedValue: { label: 'Should Contain', hint: 'The substring the actual value must contain', placeholder: 'e.g. logged in' },
  },
  'Hard Assert Not Contains': {
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to check', placeholder: 'e.g. {{step1.message}}' },
    expectedValue: { label: 'Must Not Contain', hint: 'The substring the actual value must NOT contain', placeholder: 'e.g. error' },
  },
  'Hard Assert Contains Ignore Case': {
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to check', placeholder: 'e.g. {{step1.message}}' },
    expectedValue: { label: 'Should Contain (any case)', hint: 'Case-insensitive — passes if actual contains expected regardless of case', placeholder: 'e.g. Success' },
  },
  'Hard Assert Matches Regex': {
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to check', placeholder: 'e.g. {{step1.orderId}}' },
    expectedValue: { label: 'Regex Pattern', hint: 'Regular expression — fails if not matched. Invalid regex also fails gracefully.', placeholder: 'e.g. ^ORD-\\d{6}$' },
  },

  // --- Soft Assertions ---
  'Soft Assert Equals': {
    expectedValue: { label: 'Expected Value', hint: 'The value you expect', placeholder: 'e.g. 10' },
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to compare', placeholder: 'e.g. productCount' },
  },
  'Soft Assert Not Equals': {
    expectedValue: { label: 'Not Expected Value', hint: 'The value it should NOT be', placeholder: 'e.g. pending' },
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to compare', placeholder: 'e.g. orderStatus' },
  },
  'Soft Assert True': {
    actualValue: { label: 'Value to Check', hint: 'Should resolve to "true"', placeholder: 'e.g. featureEnabled' },
  },
  'Soft Assert False': {
    actualValue: { label: 'Value to Check', hint: 'Should resolve to "false"', placeholder: 'e.g. maintenanceMode' },
  },
  'Soft Assert Null': {
    actualValue: { label: 'Variable to Check', hint: 'Should be null or empty', placeholder: 'e.g. optionalField' },
  },
  'Soft Assert Not Null': {
    actualValue: { label: 'Variable to Check', hint: 'Should NOT be null or empty', placeholder: 'e.g. responseData' },
  },
  'Soft Assert Contains': {
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to check', placeholder: 'e.g. {{step1.message}}' },
    expectedValue: { label: 'Should Contain', hint: 'The substring the actual value must contain', placeholder: 'e.g. logged in' },
  },
  'Soft Assert Not Contains': {
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to check', placeholder: 'e.g. {{step1.message}}' },
    expectedValue: { label: 'Must Not Contain', hint: 'The substring the actual value must NOT contain', placeholder: 'e.g. error' },
  },
  'Soft Assert Contains Ignore Case': {
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to check', placeholder: 'e.g. {{step1.message}}' },
    expectedValue: { label: 'Should Contain (any case)', hint: 'Case-insensitive — passes if actual contains expected regardless of case', placeholder: 'e.g. Success' },
  },
  'Soft Assert Matches Regex': {
    actualValue: { label: 'Actual Value / Variable', hint: 'Variable name or value to check', placeholder: 'e.g. {{step1.orderId}}' },
    expectedValue: { label: 'Regex Pattern', hint: 'Regular expression — fails if not matched. Invalid regex also fails gracefully.', placeholder: 'e.g. ^\\d{4}-\\d{2}-\\d{2}$' },
  },

  // --- Wait Operations ---
  'Wait': {
    timeout: { label: 'Wait Duration', hint: 'How long to pause in milliseconds' },
  },
  'Wait for Loading to Complete': {
    locator: { label: 'Loader Element', hint: 'The loading spinner or indicator to wait for', placeholder: 'e.g. .loading-spinner, #loader' },
  },
  'Wait for Loader Cycle': {
    locator: { label: 'Loader Element', hint: 'The loader that will appear then disappear', placeholder: 'e.g. .page-loader, #spinner' },
  },

  // --- Table Operations ---
  'Check if Table Contains Value': {
    searchValue: { label: 'Value to Find', hint: 'Text to search for anywhere in the table', placeholder: 'e.g. user@example.com' },
  },
  'Click Element in Table Cell': {
    searchColumnName: { label: 'Search In Column', hint: 'Which column to find the row', placeholder: 'e.g. Email, Name' },
    columnValue: { label: 'Row Matching', hint: 'Find the row where this column has this value', placeholder: 'e.g. john@example.com' },
    columnName: { label: 'Click In Column', hint: 'Which column contains the button/link to click', placeholder: 'e.g. Actions' },
    elementType: { label: 'Element to Click', hint: 'HTML tag to click in that cell', placeholder: 'e.g. button, a, input' },
  },
  'Click Table Header': {
    columnName: { label: 'Header to Click', hint: 'Click this column header (e.g. to sort)', placeholder: 'e.g. Name, Date, Status' },
  },
  'Filter Table': {
    filterValue: { label: 'Filter By', hint: 'Value to filter the table with', placeholder: 'e.g. Active, Completed' },
  },
  'Find Table Row by Text': {
    textToEnter: { label: 'Text to Find', hint: 'The text to search for in the table row', placeholder: 'e.g. Conway, John' },
  },
  'Navigate Table Pagination': {
    paginationAction: { label: 'Go To', hint: 'next, previous, first, last, or a page number', placeholder: 'e.g. next, 3' },
  },
  'Extract Value from Table': {
    searchColumnName: { label: 'Find Row In Column', hint: 'Which column to search for the row', placeholder: 'e.g. Product' },
    columnValue: { label: 'Row Matching', hint: 'Find the row where this value appears', placeholder: 'e.g. Widget' },
    columnName: { label: 'Extract From Column', hint: 'Which column to extract the value from', placeholder: 'e.g. Price' },
    variableName: { label: 'Save As', hint: 'Name to reuse the extracted value later', placeholder: 'e.g. widgetPrice' },
  },
  'Get Table Cell Data': {
    searchColumnName: { label: 'Search In Column', hint: 'Which column to find the row', placeholder: 'e.g. Last Name, Product Name' },
    columnValue: { label: 'Row Matching', hint: 'Find the row where this column has this value', placeholder: 'e.g. Smith, Widget Pro' },
    columnName: { label: 'Get From Column', hint: 'Which column to read the cell value from', placeholder: 'e.g. First Name, Price' },
  },
  'Get Table Cell Element': {
    searchColumnName: { label: 'Search In Column', hint: 'Which column to find the row', placeholder: 'e.g. Last Name' },
    columnValue: { label: 'Row Matching', hint: 'Find the row where this column has this value', placeholder: 'e.g. Smith' },
    columnName: { label: 'Element In Column', hint: 'Which column the element is in', placeholder: 'e.g. Actions' },
  },
  'Get Table Row by Column Value': {
    columnName: { label: 'Column to Search', hint: 'Which column to look in', placeholder: 'e.g. Status' },
    columnValue: { label: 'Value to Match', hint: 'Find the row with this value', placeholder: 'e.g. Pending' },
  },
  'Find All Matching Rows in Table': {
    columnName: { label: 'Column to Search', hint: 'Which column to look in', placeholder: 'e.g. Status' },
    columnValue: { label: 'Value to Match', hint: 'What to search for in that column', placeholder: 'e.g. Completed' },
  },
  'Get Table Row Data': {
    targetValue: { label: 'Row Number', hint: 'Which row to get data from', placeholder: 'e.g. 1, 5' },
  },
  'Select Table Checkbox': {
    targetValue: { label: 'Row Number', hint: 'Which row to select the checkbox in', placeholder: 'e.g. 1, 3' },
  },
  'Select from Table Dropdown': {
    targetValue: { label: 'Row Number', hint: 'Which row has the dropdown', placeholder: 'e.g. 2' },
    additionalValue: { label: 'Column Name', hint: 'Which column has the dropdown', placeholder: 'e.g. Priority, Status' },
    textToEnter: { label: 'Select Option', hint: 'The option to choose from the dropdown', placeholder: 'e.g. Approved' },
  },
  'Verify Table Sort Order': {
    columnName: { label: 'Column to Check', hint: 'Which column to verify the sort order of', placeholder: 'e.g. Name, Date' },
  },

  // --- Alerts & Popups ---
  'Get Alert Text': {
    variableName: { label: 'Save Alert Text As', hint: 'Name to reuse the alert message later', placeholder: 'e.g. alertMessage' },
  },
  'Type into Alert': {
    expectedValue: { label: 'Alert Input Text', hint: 'Text to type into the browser alert prompt', placeholder: 'e.g. my response' },
  },

  // --- File Operations ---
  'Upload a File': {
    textToEnter: { label: 'File Path', hint: 'Full path to the file on disk', placeholder: 'e.g. C:\\files\\document.pdf' },
  },
  'Upload Multiple Files': {
    textToEnter: { label: 'File Paths', hint: 'Comma-separated paths to files', placeholder: 'e.g. C:\\files\\a.pdf, C:\\files\\b.png' },
  },

  // --- Frame & Window ---
  'Switch to iFrame': {
    locator: { label: 'iFrame Selector', hint: 'The selector pointing to the iframe element', placeholder: 'e.g. #content-frame, iframe[name="main"]' },
  },
  'Switch to Window/Tab': {
    expectedValue: { label: 'Switch by', hint: 'How to identify the window or tab to switch to', placeholder: '' },
  },

  // --- Advanced ---
  'Take Screenshot': {
    variableName: { label: 'Save Screenshot As', hint: 'Variable name to store the screenshot path', placeholder: 'e.g. dashboardScreenshot' },
  },
  'Execute JavaScript': {
    expectedValue: { label: 'JavaScript Code', hint: 'Code to run in the browser (use "return" to get a value)', placeholder: 'e.g. return document.title' },
    variableName: { label: 'Save Result As', hint: 'Store the return value for later use', placeholder: 'e.g. jsResult' },
  },
  'Set Window Size': {
    expectedValue: { label: 'Window Size', hint: 'Width and height in pixels', placeholder: 'e.g. 1920,1080' },
  },

  // --- Data & Math ---
  'Apply Regex': {
    textToEnter:   { label: 'Input Value',    hint: 'The text to search in — use {{variable}} for extracted values', placeholder: 'e.g. {{pageText}}' },
    expectedValue: { label: 'Pattern',        hint: 'Java regex — wrap the part you want in (...); returns group 1 or the whole match if no groups', placeholder: 'e.g. Total (\\d+)' },
    variableName:  { label: 'Save match as',  hint: 'Reuse the matched value with {{variableName}}', placeholder: 'e.g. total' },
  },
  'Parse to Number': {
    textToEnter:  { label: 'Value',          hint: 'Must already be a bare number — use Apply Regex first if it contains text or formatting like $1,234', placeholder: 'e.g. {{total}}' },
    variableName: { label: 'Save number as', hint: 'Reuse the numeric value with {{variableName}}', placeholder: 'e.g. totalNum' },
  },
  'Calculate': {
    expectedValue: { label: 'Expression', hint: 'Use {{variables}}, operators + − × ÷ % ^, and functions: round, min, max, pow, sqrt, abs, ceil, floor', placeholder: 'e.g. round({{totalNum}} * 1.15, 2)' },
    variableName:  { label: 'Save result as', hint: 'Reuse the result with {{variableName}}', placeholder: 'e.g. withTax' },
  },
};

// Pre-grouped actions by category for the dropdown
export const WEB_UI_ACTIONS_BY_CATEGORY = Object.entries(WEB_UI_ACTIONS).reduce((acc, [key, action]) => {
  if (!acc[action.category]) acc[action.category] = [];
  acc[action.category].push({ value: key, ...action });
  return acc;
}, {});

// Actions hidden from the action picker (legacy/deprecated — still execute if already in a test)
export const DEPRECATED_WEB_UI_ACTIONS = new Set(['Scroll Element into View']);

// Actions that render a keyboard key dropdown instead of free text for expectedValue
export const KEYBOARD_KEY_ACTIONS = new Set(['Press Keyboard Key', 'Press Key Down', 'Release Key Up']);

// Actions where the keyboard key dropdown replaces the textToEnter field (split layout, right pane)
export const KEYBOARD_TEXT_ENTER_ACTIONS = new Set(['Send Keyboard Keys']);

// Actions that render the Switch-By dropdown instead of a plain expectedValue text field
export const SWITCH_BY_FIELD_ACTIONS = new Set(['Switch to Window/Tab']);

// Actions with fully custom Data/Math UI (no locator combo)
export const DATA_MATH_ACTIONS = new Set(['Apply Regex', 'Parse to Number', 'Calculate']);

// ===== Stop & Exit actions (Break Loop / End Permutation) =====
// Display-name driven: the backend matches on these exact strings, so they double
// as the action key and the dropdown label. Shared by the Web-UI and Mobile
// engines — the same action string on both.
export const BREAK_LOOP_ACTION = 'Break Loop';
export const END_PERMUTATION_ACTION = 'End Permutation';

// Bare and unconditional: when execution reaches one, it fires. Both engines have
// had per-action condition evaluation removed, so a `condition` on the action is
// dead weight — it is stripped on save, never authored, never sent. Gating is done
// by wrapping the action in a Conditional then/else branch like any other action.
export const FLOW_SIGNAL_ACTIONS = new Set([BREAK_LOOP_ACTION, END_PERMUTATION_ACTION]);

// Editor copy for each — the whole body of these actions, since they take no fields.
export const FLOW_SIGNAL_INFO = {
  [BREAK_LOOP_ACTION]: 'Exits the innermost loop this action sits in; the script continues with whatever follows the loop. Outside a loop it does nothing.',
  [END_PERMUTATION_ACTION]: 'Ends the current permutation (this data row) immediately and reports it as PASS. Remaining actions and steps are skipped; the next permutation runs normally.',
};

// Condition types for Conditional actions — full v2 set (~30 types)
// fields values:
//   'element'   → identifierType + locator + timeout
//   'text'      → identifierType + locator + expectedValue (string)
//   'attribute' → identifierType + locator + attribute + expectedValue
//   'count'     → identifierType + locator + expectedValue (integer)
//   'page'      → expectedValue only (no locator)
//   'alert'     → no fields
//   'value'     → variable + expectedValue
//   'actionRef' → actionRef (step number)
export const CONDITION_TYPES = [
  // Element presence & state
  { value: 'elementExists',      label: 'Element Exists',                 fields: 'element' },
  { value: 'elementVisible',     label: 'Element Is Visible',             fields: 'element' },
  { value: 'elementNotExists',   label: 'Element Does Not Exist',         fields: 'element' },
  { value: 'elementNotVisible',  label: 'Element Is Not Visible',         fields: 'element' },
  { value: 'elementEnabled',     label: 'Element Is Enabled',             fields: 'element' },
  { value: 'elementDisabled',    label: 'Element Is Disabled',            fields: 'element' },
  { value: 'elementSelected',    label: 'Element Is Selected / Checked',  fields: 'element' },
  { value: 'elementNotSelected', label: 'Element Is Not Selected',        fields: 'element' },
  // Text matching
  { value: 'textEquals',         label: 'Text Equals',                    fields: 'text' },
  { value: 'textContains',       label: 'Text Contains',                  fields: 'text' },
  { value: 'textStartsWith',     label: 'Text Starts With',               fields: 'text' },
  { value: 'textEndsWith',       label: 'Text Ends With',                 fields: 'text' },
  // Input value
  { value: 'inputValueEquals',   label: 'Input Value Equals',             fields: 'text' },
  { value: 'inputValueContains', label: 'Input Value Contains',           fields: 'text' },
  // Attribute & class
  { value: 'attributeEquals',    label: 'Attribute Equals',               fields: 'attribute' },
  { value: 'attributeContains',  label: 'Attribute Contains',             fields: 'attribute' },
  { value: 'hasClass',           label: 'Has CSS Class',                  fields: 'text' },
  { value: 'notHasClass',        label: 'Does Not Have CSS Class',        fields: 'text' },
  // Counts
  { value: 'countEquals',        label: 'Element Count Equals',           fields: 'count' },
  { value: 'countGreaterThan',   label: 'Element Count Greater Than',     fields: 'count' },
  { value: 'countLessThan',      label: 'Element Count Less Than',        fields: 'count' },
  // Page state
  { value: 'urlEquals',          label: 'URL Equals',                     fields: 'page' },
  { value: 'urlContains',        label: 'URL Contains',                   fields: 'page' },
  { value: 'titleEquals',        label: 'Page Title Equals',              fields: 'page' },
  { value: 'titleContains',      label: 'Page Title Contains',            fields: 'page' },
  { value: 'alertPresent',       label: 'Alert Is Present',               fields: 'alert' },
  // Variable conditions
  { value: 'valueEquals',        label: 'Value Equals',                   fields: 'value' },
  { value: 'valueNotEquals',     label: 'Value Does Not Equal',           fields: 'value' },
  { value: 'valueContains',      label: 'Value Contains',                 fields: 'value' },
  { value: 'valueGreaterThan',   label: 'Value Greater Than (numeric)',   fields: 'value' },
  { value: 'valueLessThan',      label: 'Value Less Than (numeric)',      fields: 'value' },
  // Action result
  { value: 'actionPassed',       label: 'Previous Action Passed',         fields: 'actionRef' },
  { value: 'actionFailed',       label: 'Previous Action Failed',         fields: 'actionRef' },
];

// Map of condition type → its field category (element | text | attribute | count |
// page | alert | value | actionRef). Single source of truth used by the editor to
// decide which inputs to render and by the serializer to keep only relevant fields.
export const CONDITION_TYPE_FIELDS = Object.fromEntries(CONDITION_TYPES.map((ct) => [ct.value, ct.fields]));

// Helper: blank condition object — shared by Conditional branches, loop /
// ForEach stopCondition, and the Stop & Exit actions' optional condition.
export const blankWebUiCondition = () => ({
  type: '', identifierType: 'css', locator: '', variable: '', expectedValue: '',
  attribute: '', actionRef: '', timeout: 3000,
});

// Factory for creating a default Web UI action (v2 shapes for new control-flow actions)
export const createDefaultWebUiAction = (actionType = '') => {
  if (actionType === 'Conditional') {
    // v2 shape: branches[]
    return {
      id: `wui-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      action: 'Conditional',
      description: '',
      disabled: false,
      screenshotsPerIteration: 'all',
      branches: [
        {
          id: `wui-br-${Date.now()}-1`,
          type: 'if',
          condition: blankWebUiCondition(),
          actions: [],
        },
      ],
    };
  }
  if (actionType === 'Loop') {
    // v2 shape: loop.pattern
    return {
      id: `wui-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      action: 'Loop',
      description: '',
      disabled: false,
      screenshotsPerIteration: 'all',
      loop: {
        pattern: 'retry',
        stopCondition: blankWebUiCondition(),
        maxIterations: 10,
        delayMs: 0,
      },
      actions: [],
    };
  }
  if (FLOW_SIGNAL_ACTIONS.has(actionType)) {
    // Takes no fields at all — the payload is just { action, description? }.
    return {
      id: `wui-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      action: actionType,
      description: '',
      disabled: false,
    };
  }
  if (actionType === 'ForEach') {
    return {
      id: `wui-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      action: 'ForEach',
      description: '',
      disabled: false,
      screenshotsPerIteration: 'all',
      foreach: {
        identifierType: 'css',
        locator: '',
        stopWhen: 'fixed',
        maxItems: 100,
        delayMs: 0,
        stopCondition: blankWebUiCondition(),
      },
      actions: [],
    };
  }
  return {
    id: `wui-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    action: actionType,
    description: '',
    disabled: false,
    identifierType: 'css',
    locator: '',
    textToEnter: '',
    expectedValue: '',
    actualValue: '',
    targetValue: '',
    variableName: '',
    timeout: '',
    additionalValue: '',
    searchColumnName: '',
    searchValue: '',
    columnName: '',
    columnValue: '',
    elementType: '',
    filterValue: '',
    paginationAction: '',
    sortOrder: '',
    dataType: '',
    xOffset: '',
    yOffset: '',
  };
};

// ===== Mobile Device Configuration =====

export const MOBILE_PLATFORMS = [
  { value: 'Android', label: 'Android' },
  { value: 'iOS',     label: 'iOS' },
];

// Android connection types
export const ANDROID_CONNECTION_TYPES = [
  { value: 'emulator', label: 'Emulator',        hint: 'Local Android emulator (EC2 Mac)' },
  { value: 'physical', label: 'Physical Device', hint: 'Physical Android device (Physical Mac)' },
];

// iOS connection types
export const IOS_CONNECTION_TYPES = [
  { value: 'simulator', label: 'Simulator',       hint: 'Local iOS simulator (EC2 Mac)' },
  { value: 'physical',  label: 'Physical Device', hint: 'Physical iOS device (Physical Mac)' },
];

// Legacy alias — used by existing code that doesn't care about platform
export const MOBILE_CONNECTION_TYPES = ANDROID_CONNECTION_TYPES;

// Physical devices available on the Physical Mac tier
export const PHYSICAL_DEVICES = [
  {
    value: 'iphone-13-pro',
    label: 'iPhone 13 Pro',
    platformName: 'iOS',
    deviceName: 'iPhone 13 Pro',
    udid: '00008110-001078DC2622401E',
    platformVersion: '18.6',
    xcodeOrgId: 'QMSX3B5VJG',
    xcodeSigningId: 'Apple Development',
  },
  {
    value: 'iphone-14-pro',
    label: 'iPhone 14 Pro',
    platformName: 'iOS',
    deviceName: 'iPhone 14 Pro',
    udid: '00008120-001938600244201E',
    platformVersion: '26.3.1',
    xcodeOrgId: 'QMSX3B5VJG',
    xcodeSigningId: 'Apple Development',
  },
  {
    value: 'samsung-a25',
    label: 'Samsung Galaxy A25 5G',
    platformName: 'Android',
    deviceName: 'Samsung Galaxy A25 5G',
    udid: 'RZCX809VW5H',
    platformVersion: '14',
  },
];

// Android app source types
export const ANDROID_APP_SOURCE_TYPES = [
  { value: 'apk',     label: 'Install APK',       hint: 'Upload & install APK from S3' },
  { value: 'package', label: 'Built-in App',      hint: 'Launch by package + activity' },
  { value: 'browser', label: 'Mobile Browser',    hint: 'Run Chrome browser test' },
];

// iOS app source types
export const IOS_APP_SOURCE_TYPES = [
  { value: 'bundle',  label: 'Built-in App',      hint: 'Launch Apple or pre-installed app by bundle ID' },
  { value: 'appzip',  label: 'Install .app.zip',  hint: 'Upload & install custom .app.zip from S3' },
  { value: 'browser', label: 'Safari Browser',    hint: 'Run Safari browser test' },
];

// Legacy alias
export const MOBILE_APP_SOURCE_TYPES = ANDROID_APP_SOURCE_TYPES;

// Android device names
export const ANDROID_DEVICE_NAMES = [
  { value: 'Pixel_8',     label: 'Pixel 8',     android: '14', api: '34' },
  { value: 'Galaxy_S24',  label: 'Galaxy S24',  android: '14', api: '34' },
  { value: 'OnePlus_12',  label: 'OnePlus 12',  android: '14', api: '34' },
  { value: 'Pixel_7',     label: 'Pixel 7',     android: '13', api: '33' },
  { value: 'test-device', label: 'Test Device', android: '14', api: '34' },
];

// iOS device names
export const IOS_DEVICE_NAMES = [
  { value: 'iPhone_15',         label: 'iPhone 15',              ios: '18.6' },
  { value: 'iPhone_15_Pro',     label: 'iPhone 15 Pro',          ios: '18.6' },
  { value: 'iPhone_15_Pro_Max', label: 'iPhone 15 Pro Max',      ios: '18.6' },
  { value: 'iPhone_15_Plus',    label: 'iPhone 15 Plus',         ios: '18.6' },
  { value: 'iPhone_SE',         label: 'iPhone SE (3rd gen)',     ios: '18.6' },
  { value: 'iPad_Pro_13_M4',    label: 'iPad Pro 13-inch (M4)',  ios: '18.6' },
  { value: 'iPad_Pro_11_M4',    label: 'iPad Pro 11-inch (M4)',  ios: '18.6' },
];

// Legacy alias
export const MOBILE_DEVICE_NAMES = ANDROID_DEVICE_NAMES;

// ===== Mobile Action Constants =====

export const MOBILE_ACTION_CATEGORIES = {
  INTERACTIONS:   { label: 'Touch & Tap',               color: '#3b82f6' },
  ELEMENT_STATE:  { label: 'Element State',             color: '#f59e0b' },
  SELECTION:      { label: 'Dropdowns & Pickers',       color: '#ec4899' },
  KEYBOARD:       { label: 'Keyboard & Keys',           color: '#8b5cf6' },
  NAVIGATION:     { label: 'Navigation',                color: '#22c55e' },
  SCROLL:         { label: 'Scroll',                   color: '#a855f7' },
  SWIPE:          { label: 'Swipe',                    color: '#8b5cf6' },
  ZOOM:           { label: 'Zoom',                     color: '#06b6d4' },
  TABLE_OPS:      { label: 'Tables & Lists',            color: '#06b6d4' },
  READ_SCREEN:    { label: 'Read from Screen',          color: '#10b981' },
  VERIFICATIONS:  { label: 'Verification',              color: '#14b8a6' },
  HARD_ASSERT:    { label: 'Hard Assertions',           color: '#ef4444' },
  SOFT_ASSERT:    { label: 'Soft Assertions',           color: '#f97316' },
  WAIT_OPS:       { label: 'Waits',                     color: '#6366f1' },
  ALERTS:         { label: 'Alerts & Notifications',    color: '#d97706' },
  FILE_OPS:       { label: 'Files & Media',             color: '#0ea5e9' },
  CONTEXT:        { label: 'Context Switching',         color: '#a855f7' },
  APP_LIFECYCLE:  { label: 'App Lifecycle',             color: '#64748b' },
  DEVICE:         { label: 'Device Controls',           color: '#475569' },
  CONTROL_FLOW:   { label: 'Control Flow',              color: '#f59e0b' },
  // See the Web-UI note above — kept out of CONTROL_FLOW so nested action pickers
  // (which hide that category) still offer Break Loop / End Permutation.
  FLOW_SIGNAL:    { label: 'Stop & Exit',               color: '#e11d48' },
  DATA_MATH:      { label: 'Data & Math',               color: '#0ea5e9' },
  ADVANCED:       { label: 'Advanced',                  color: '#64748b' },
  ENVIRONMENT:    { label: 'Environment & Simulation',  color: '#7c3aed' },
};

export const MOBILE_ACTIONS = {
  // --- Interactions: Touch (4) ---
  'Tap':                  { category: 'INTERACTIONS', label: 'Tap / Click',           requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Double Tap':           { category: 'INTERACTIONS', label: 'Double Tap / Double Click', requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Long Press':           { category: 'INTERACTIONS', label: 'Long Press',           requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'pressDurationMs', 'timeout'] },
  'Drag and Drop':        { category: 'INTERACTIONS', label: 'Drag and Drop',        requiredFields: ['identifierType', 'locator', 'additionalValue'], optionalFields: ['description', 'timeout'] },

  // --- Keyboard & Keys (4) ---
  'Type Text':            { category: 'KEYBOARD', label: 'Type Text',            requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Clear Text':           { category: 'KEYBOARD', label: 'Clear Text',           requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Hide Keyboard':        { category: 'KEYBOARD', label: 'Hide Keyboard',        requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Press Key':            { category: 'KEYBOARD', label: 'Press Key',            requiredFields: ['keyCode'], optionalFields: ['description'] },

  // --- Navigation (5) ---
  'Open URL':             { category: 'NAVIGATION', label: 'Open URL',             requiredFields: ['url'], optionalFields: ['description', 'timeout'] },
  'Go Back':              { category: 'NAVIGATION', label: 'Go Back',              requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Go Forward':           { category: 'NAVIGATION', label: 'Go Forward',           requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Refresh Page':         { category: 'NAVIGATION', label: 'Refresh Page',         requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Trigger Deep Link':    { category: 'NAVIGATION', label: 'Open App Screen (Deep Link)', requiredFields: ['url'], optionalFields: ['appPackage', 'description', 'timeout'] },

  // --- Gestures: Scroll (5) — moves within content; the most-used gesture ---
  'Scroll to Element':    { category: 'SCROLL', label: 'Scroll to Element',    requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Scroll by Direction':  { category: 'SCROLL', label: 'Scroll by Direction',  requiredFields: ['scrollDirection'], optionalFields: ['swipeDurationMs', 'retryCount', 'description', 'timeout'] },
  'Scroll to End':        { category: 'SCROLL', label: 'Scroll to End',        requiredFields: ['scrollDirection'], optionalFields: ['swipeDurationMs', 'retryCount', 'description', 'timeout'] },
  'Scroll Page Up':       { category: 'SCROLL', label: 'Scroll Page Up',       requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Scroll Page Down':     { category: 'SCROLL', label: 'Scroll Page Down',     requiredFields: [], optionalFields: ['description', 'timeout'] },

  // --- Gestures: Swipe (2) — fast flick that can fling past the content edge ---
  'Swipe':                { category: 'SWIPE', label: 'Swipe',                 requiredFields: ['swipeDirection'], optionalFields: ['swipeDurationMs', 'description'] },
  'Swipe on Element':     { category: 'SWIPE', label: 'Swipe on Element',       requiredFields: ['identifierType', 'locator', 'swipeDirection'], optionalFields: ['swipeDurationMs', 'description', 'timeout'] },

  // --- Gestures: Zoom (2) ---
  'Zoom':                 { category: 'ZOOM', label: 'Zoom In',                requiredFields: ['identifierType', 'locator'], optionalFields: ['pinchScale', 'description', 'timeout'] },
  'Pinch':                { category: 'ZOOM', label: 'Zoom Out',               requiredFields: ['identifierType', 'locator'], optionalFields: ['pinchScale', 'description', 'timeout'] },

  // --- Element State (5) ---
  'Check':                { category: 'ELEMENT_STATE', label: 'Check',               requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Uncheck':              { category: 'ELEMENT_STATE', label: 'Uncheck',             requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Toggle Switch':        { category: 'ELEMENT_STATE', label: 'Toggle Switch',       requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Focus on Element':     { category: 'ELEMENT_STATE', label: 'Focus on Element',    requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Remove Focus':         { category: 'ELEMENT_STATE', label: 'Remove Focus',        requiredFields: [], optionalFields: ['description', 'timeout'] },

  // --- Dropdowns & Pickers (4) ---
  'Select from Dropdown':   { category: 'SELECTION', label: 'Select from Dropdown',   requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Select by Position':     { category: 'SELECTION', label: 'Select by Position',     requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Select by Value':        { category: 'SELECTION', label: 'Select by Value',        requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Select by Display Text': { category: 'SELECTION', label: 'Select by Display Text', requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },

  // --- Tables & Lists: Read (8) ---
  'Get All Data':                 { category: 'TABLE_OPS', label: 'Get All Data',                 requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Row Data':                 { category: 'TABLE_OPS', label: 'Get Row Data',                 requiredFields: ['identifierType', 'locator', 'targetValue'], optionalFields: ['variableName', 'description', 'timeout'] },
  'Get Row by Column Value':      { category: 'TABLE_OPS', label: 'Get Row by Column Value',      requiredFields: ['identifierType', 'locator', 'columnName', 'columnValue', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Column Data':              { category: 'TABLE_OPS', label: 'Get Column Data',              requiredFields: ['identifierType', 'locator', 'columnName', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Column Count':             { category: 'TABLE_OPS', label: 'Get Column Count',             requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Row Count':                { category: 'TABLE_OPS', label: 'Get Row Count',                requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Table Headers':            { category: 'TABLE_OPS', label: 'Get Table Headers',            requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Extract Value from Table':     { category: 'TABLE_OPS', label: 'Extract Value from Table',     requiredFields: ['identifierType', 'locator', 'columnName', 'targetValue', 'variableName'], optionalFields: ['description', 'timeout'] },

  // --- Tables & Lists: Interact (8) ---
  'Click Element in Row':         { category: 'TABLE_OPS', label: 'Click Element in Row',         requiredFields: ['identifierType', 'locator', 'columnName', 'columnValue', 'targetValue'], optionalFields: ['description', 'timeout'] },
  'Click Table Header':           { category: 'TABLE_OPS', label: 'Click Table Header',           requiredFields: ['identifierType', 'locator', 'columnName'], optionalFields: ['description', 'timeout'] },
  // 'Filter Table':                 { category: 'TABLE_OPS', label: 'Filter Table',                 requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Find Row by Text':             { category: 'TABLE_OPS', label: 'Find Row by Text',             requiredFields: ['identifierType', 'locator', 'textToEnter', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Find All Matching Rows':       { category: 'TABLE_OPS', label: 'Find All Matching Rows',       requiredFields: ['identifierType', 'locator', 'textToEnter', 'variableName'], optionalFields: ['description', 'timeout'] },
  //'Navigate Pagination':          { category: 'TABLE_OPS', label: 'Navigate Pagination',          requiredFields: ['textToEnter'], optionalFields: ['description', 'timeout'] },
 // 'Select Row Checkbox':          { category: 'TABLE_OPS', label: 'Select Row Checkbox',          requiredFields: ['identifierType', 'locator', 'columnName'], optionalFields: ['columnValue', 'description', 'timeout'] },
  'Select from Row Dropdown':     { category: 'TABLE_OPS', label: 'Select from Row Dropdown',     requiredFields: ['identifierType', 'locator', 'columnName', 'textToEnter'], optionalFields: ['columnValue', 'description', 'timeout'] },

  // --- Read from Screen (6) ---
  'Get Text':             { category: 'READ_SCREEN', label: 'Get Text',             requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Attribute':        { category: 'READ_SCREEN', label: 'Get Attribute',        requiredFields: ['identifierType', 'locator', 'targetValue', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Element Count':    { category: 'READ_SCREEN', label: 'Get Element Count',    requiredFields: ['identifierType', 'locator', 'variableName'], optionalFields: ['description', 'timeout'] },
  'Get Current Activity': { category: 'READ_SCREEN', label: 'Get Current Activity', requiredFields: ['variableName'], optionalFields: ['description'] },
  'Get Current URL':      { category: 'READ_SCREEN', label: 'Get Current URL',      requiredFields: ['variableName'], optionalFields: ['description'] },
  'Get Page Source':      { category: 'READ_SCREEN', label: 'Get Page Source',      requiredFields: ['variableName'], optionalFields: ['description'] },

  // --- Verification (9) ---
  'Verify Exists':        { category: 'VERIFICATIONS', label: 'Verify Exists',        requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Visible':       { category: 'VERIFICATIONS', label: 'Verify Visible',       requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Not Visible':   { category: 'VERIFICATIONS', label: 'Verify Not Visible',   requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Enabled':       { category: 'VERIFICATIONS', label: 'Verify Enabled',       requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Disabled':      { category: 'VERIFICATIONS', label: 'Verify Disabled',      requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Checked':       { category: 'VERIFICATIONS', label: 'Verify Checked',       requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Verify Text Content':  { category: 'VERIFICATIONS', label: 'Verify Text Content',  requiredFields: ['identifierType', 'locator', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Verify Text Contains': { category: 'VERIFICATIONS', label: 'Verify Text Contains', requiredFields: ['identifierType', 'locator', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Verify Attribute':     { category: 'VERIFICATIONS', label: 'Verify Attribute',     requiredFields: ['identifierType', 'locator', 'targetValue'], optionalFields: ['expectedValue', 'description', 'timeout'] },

  // --- Hard Assertions (10) ---
  'Hard Assert Equals':               { category: 'HARD_ASSERT', label: 'Hard Assert Equals',               requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },
  'Hard Assert Not Equals':           { category: 'HARD_ASSERT', label: 'Hard Assert Not Equals',           requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },
  'Hard Assert True':                 { category: 'HARD_ASSERT', label: 'Hard Assert True',                 requiredFields: ['actualValue'], optionalFields: ['description'] },
  'Hard Assert False':                { category: 'HARD_ASSERT', label: 'Hard Assert False',                requiredFields: ['actualValue'], optionalFields: ['description'] },
  'Hard Assert Null':                 { category: 'HARD_ASSERT', label: 'Hard Assert Null',                 requiredFields: ['actualValue'], optionalFields: ['description'] },
  'Hard Assert Not Null':             { category: 'HARD_ASSERT', label: 'Hard Assert Not Null',             requiredFields: ['actualValue'], optionalFields: ['description'] },
  'Hard Assert Contains':             { category: 'HARD_ASSERT', label: 'Hard Assert Contains',             requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },
  'Hard Assert Not Contains':         { category: 'HARD_ASSERT', label: 'Hard Assert Not Contains',         requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },
  'Hard Assert Contains Ignore Case': { category: 'HARD_ASSERT', label: 'Hard Assert Contains Ignore Case', requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },
  'Hard Assert Matches Regex':        { category: 'HARD_ASSERT', label: 'Hard Assert Matches Regex',        requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },

  // --- Soft Assertions (10) ---
  'Soft Assert Equals':               { category: 'SOFT_ASSERT', label: 'Soft Assert Equals',               requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },
  'Soft Assert Not Equals':           { category: 'SOFT_ASSERT', label: 'Soft Assert Not Equals',           requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },
  'Soft Assert True':                 { category: 'SOFT_ASSERT', label: 'Soft Assert True',                 requiredFields: ['actualValue'], optionalFields: ['description'] },
  'Soft Assert False':                { category: 'SOFT_ASSERT', label: 'Soft Assert False',                requiredFields: ['actualValue'], optionalFields: ['description'] },
  'Soft Assert Null':                 { category: 'SOFT_ASSERT', label: 'Soft Assert Null',                 requiredFields: ['actualValue'], optionalFields: ['description'] },
  'Soft Assert Not Null':             { category: 'SOFT_ASSERT', label: 'Soft Assert Not Null',             requiredFields: ['actualValue'], optionalFields: ['description'] },
  'Soft Assert Contains':             { category: 'SOFT_ASSERT', label: 'Soft Assert Contains',             requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },
  'Soft Assert Not Contains':         { category: 'SOFT_ASSERT', label: 'Soft Assert Not Contains',         requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },
  'Soft Assert Contains Ignore Case': { category: 'SOFT_ASSERT', label: 'Soft Assert Contains Ignore Case', requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },
  'Soft Assert Matches Regex':        { category: 'SOFT_ASSERT', label: 'Soft Assert Matches Regex',        requiredFields: ['actualValue', 'expectedValue'], optionalFields: ['description'] },

  // --- Waits (11) ---
  'Wait':                           { category: 'WAIT_OPS', label: 'Wait',                           requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Wait for Element':               { category: 'WAIT_OPS', label: 'Wait for Element to Appear',               requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Visible':               { category: 'WAIT_OPS', label: 'Wait for Element to be Visible',               requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Gone':                  { category: 'WAIT_OPS', label: 'Wait for Element to be Gone',                  requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Enabled':               { category: 'WAIT_OPS', label: 'Wait for Element to be Enabled',               requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Text':                  { category: 'WAIT_OPS', label: 'Wait for Text',                  requiredFields: ['identifierType', 'locator', 'expectedValue'], optionalFields: ['description', 'timeout'] },
  'Wait for Clickable':             { category: 'WAIT_OPS', label: 'Wait for Element to be Clickable',             requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Loading':               { category: 'WAIT_OPS', label: 'Wait for Loading',               requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Loader Cycle':          { category: 'WAIT_OPS', label: 'Wait for Loader Cycle',          requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for Element (No Loader)':   { category: 'WAIT_OPS', label: 'Wait for Element (No Loader)',   requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },
  'Wait for All Loaders Gone':      { category: 'WAIT_OPS', label: 'Wait for All Loaders Gone',      requiredFields: ['identifierType', 'locator'], optionalFields: ['description', 'timeout'] },

  // --- Alerts (4) ---
  'Accept Alert':           { category: 'ALERTS', label: 'Accept Alert',           requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Dismiss Alert':          { category: 'ALERTS', label: 'Dismiss Alert',          requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Get Alert Text':         { category: 'ALERTS', label: 'Get Alert Text',         requiredFields: ['variableName'], optionalFields: ['description', 'timeout'] },
  'Type into Alert':        { category: 'ALERTS', label: 'Type into Alert',        requiredFields: ['textToEnter'], optionalFields: ['description', 'timeout'] },

  // --- Notifications (3) ---
  'Open Notifications':   { category: 'ALERTS', label: 'Open Notifications',  requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Tap Notification':     { category: 'ALERTS', label: 'Tap / Click Notification', requiredFields: ['textToEnter'], optionalFields: ['description', 'timeout'] },
  'Dismiss Notification': { category: 'ALERTS', label: 'Dismiss Notification',requiredFields: ['textToEnter'], optionalFields: ['description', 'timeout'] },

  // --- Files & Media (2) ---
  'Upload File':            { category: 'FILE_OPS', label: 'Upload File',            requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },
  'Upload Multiple Files':  { category: 'FILE_OPS', label: 'Upload Multiple Files',  requiredFields: ['identifierType', 'locator', 'textToEnter'], optionalFields: ['description', 'timeout'] },

  // --- Context Switching (8) ---
  'Switch to WebView':      { category: 'CONTEXT', label: 'Switch to WebView',      requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Switch to Native':       { category: 'CONTEXT', label: 'Switch to Native',       requiredFields: [], optionalFields: ['description', 'timeout'] },
  // iFrame switch accepts EITHER a locator (with identifierType) OR a 1-based index in
  // textToEnter — all optional so neither path force-emits empty fields. The serializer
  // drops identifierType when no locator is supplied (index mode) so it stays clean.
  'Switch to iFrame':       { category: 'CONTEXT', label: 'Switch to iFrame',       requiredFields: [], optionalFields: ['identifierType', 'locator', 'textToEnter', 'description', 'timeout'] },
  'Switch to Window/Tab':   { category: 'CONTEXT', label: 'Switch to Window/Tab',   requiredFields: ['textToEnter'], optionalFields: ['description', 'timeout'] },
  'Switch to Main Content': { category: 'CONTEXT', label: 'Switch to Main Content', requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Switch to Parent Frame': { category: 'CONTEXT', label: 'Switch to Parent Frame', requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Close Window/Tab':       { category: 'CONTEXT', label: 'Close Window/Tab',       requiredFields: [], optionalFields: ['description', 'timeout'] },
  'Get Contexts':           { category: 'CONTEXT', label: 'Get Contexts',           requiredFields: ['variableName'], optionalFields: ['description'] },

  // --- App Lifecycle (9) ---
  // App identifier differs by platform (appPackage on Android, bundleId on iOS) — both
  // kept optional here; the editor shows the platform-appropriate one as required. No
  // appActivity: Launch App just activates the package/bundle.
  'Launch App':          { category: 'APP_LIFECYCLE', label: 'Launch Another App',  requiredFields: [], optionalFields: ['appPackage', 'bundleId', 'description', 'timeout'] },
  'Close App':           { category: 'APP_LIFECYCLE', label: 'Close App',           requiredFields: [], optionalFields: ['appPackage', 'bundleId', 'description', 'timeout'] },
  'Background App':      { category: 'APP_LIFECYCLE', label: 'Background App',      requiredFields: [], optionalFields: ['backgroundDurationSeconds', 'description'] },
  'Reset App':           { category: 'APP_LIFECYCLE', label: 'Reset App',           requiredFields: [], optionalFields: ['appPackage', 'bundleId', 'description', 'timeout'] },
  'Install App':         { category: 'APP_LIFECYCLE', label: 'Install Another App',  requiredFields: ['appPath'], optionalFields: ['description', 'timeout'] },
  'Remove App':          { category: 'APP_LIFECYCLE', label: 'Remove App',          requiredFields: ['appPackage'], optionalFields: ['bundleId', 'description', 'timeout'] },
  // App identifier differs by platform (appPackage on Android, bundleId on iOS) — the
  // editor shows the right one and only the filled one is serialized.
  'Grant Permissions':   { category: 'APP_LIFECYCLE', label: 'Grant Permissions',   requiredFields: ['permissions'], optionalFields: ['appPackage', 'bundleId', 'description'] },
  'Revoke Permissions':  { category: 'APP_LIFECYCLE', label: 'Revoke Permissions',  requiredFields: ['permissions'], optionalFields: ['appPackage', 'bundleId', 'description'] },
  'Handle Permission':   { category: 'APP_LIFECYCLE', label: 'Handle Permission',   requiredFields: ['permissionAction'], optionalFields: ['description'] },

  // --- Device Controls (5) ---
  'Press Back':    { category: 'DEVICE', label: 'Press Back',    requiredFields: [], optionalFields: ['description'] },
  'Press Home':    { category: 'DEVICE', label: 'Press Home',    requiredFields: [], optionalFields: ['description'] },
  'Lock Device':   { category: 'DEVICE', label: 'Lock Device',   requiredFields: [], optionalFields: ['description'] },
  'Unlock Device': { category: 'DEVICE', label: 'Unlock Device', requiredFields: [], optionalFields: ['description'] },
  'Rotate':        { category: 'DEVICE', label: 'Rotate',        requiredFields: ['orientation'], optionalFields: ['description'] },

  // --- Control Flow (3) ---
  'Conditional': { category: 'CONTROL_FLOW', label: 'Conditional',      requiredFields: [], optionalFields: ['description'] },
  'Loop':        { category: 'CONTROL_FLOW', label: 'Loop',             requiredFields: [], optionalFields: ['description'] },
  'ForEach':     { category: 'CONTROL_FLOW', label: 'For Each Element', requiredFields: ['identifierType', 'locator'], optionalFields: ['description'] },

  // --- Stop & Exit (2) ---
  // Same action strings and condition shape as the Web-UI engine; the mobile
  // engine reads `condition` with its own (UPPERCASE) UIConditionType set.
  'Break Loop':    { category: 'FLOW_SIGNAL', label: 'Break Loop',    requiredFields: [], optionalFields: ['description'] },
  'End Permutation': { category: 'FLOW_SIGNAL', label: 'End Permutation', requiredFields: [], optionalFields: ['description'] },

  // --- Data & Math (3) ---
  'Apply Regex':     { category: 'DATA_MATH', label: 'Apply Regex',     requiredFields: ['textToEnter', 'expectedValue', 'variableName'], optionalFields: ['description'] },
  'Parse to Number': { category: 'DATA_MATH', label: 'Parse to Number', requiredFields: ['textToEnter', 'variableName'], optionalFields: ['description'] },
  'Calculate':       { category: 'DATA_MATH', label: 'Calculate',       requiredFields: ['expectedValue', 'variableName'], optionalFields: ['description'] },

  // --- Advanced (1) ---
  'Execute Script': { category: 'ADVANCED', label: 'Execute Script', requiredFields: ['scriptCommand'], optionalFields: ['scriptArgs', 'variableName', 'description'] },

  // --- Environment: Conditions (4) ---
  'Set Network Conditions': { category: 'ENVIRONMENT', label: 'Set Network Conditions', requiredFields: ['networkCondition'], optionalFields: ['description'] },
  'Set Location':           { category: 'ENVIRONMENT', label: 'Set Location',           requiredFields: ['geoLatitude', 'geoLongitude'], optionalFields: ['geoAltitude', 'description'] },
  'Set Locale':             { category: 'ENVIRONMENT', label: 'Set Locale',             requiredFields: ['localeLanguage'], optionalFields: ['localeCountry', 'description'] },
  'Set System Date Time':   { category: 'ENVIRONMENT', label: 'Set System Date Time',   requiredFields: ['systemDateTime'], optionalFields: ['systemTimeZone', 'description'] },

  // --- Environment: Injection (3) ---
  'Send SMS':                   { category: 'ENVIRONMENT', label: 'Send SMS',                   requiredFields: ['smsMessage'], optionalFields: ['smsFromNumber', 'description'] },
  'Push File':                  { category: 'ENVIRONMENT', label: 'Push File',                  requiredFields: ['sourceFile', 'remoteFilePath'], optionalFields: ['description'] },
  'Set Media Library':          { category: 'ENVIRONMENT', label: 'Set Media Library',          requiredFields: ['sourceFile'], optionalFields: ['description'] },
};

// Back-compat for action keys whose JSON contract was renamed — maps an old stored
// `action` value to the current key so existing steps still load.
export const MOBILE_ACTION_KEY_ALIASES = {
  'Set System Date/Time': 'Set System Date Time',
  // Backend's canonical ForEach action string maps back to our internal key.
  'For Each Element': 'ForEach',
};

// Per-action field label/placeholder overrides for mobile actions.
// The field KEY and the stored JSON are unchanged — only the user-facing wording
// differs per action. e.g. Drag and Drop reuses `locator` as the drag source and
// `additionalValue` as the drop target, so they read clearly as "Drag From" / "Drop Onto".
export const MOBILE_FIELD_OVERRIDES = {
  'Drag and Drop': {
    locator:         { label: 'Drag From',  placeholder: 'Element to drag — e.g. //div[@id="item"]' },
    additionalValue: { label: 'Drop Onto',  placeholder: 'Destination — e.g. //div[@id="dropzone"]' },
  },
  // Dropdown selection variants — all store the choice in `textToEnter`, but what
  // that value MEANS differs per action, so the field reads accordingly.
  'Select from Dropdown': {
    textToEnter: { label: 'Option to select', placeholder: 'e.g. South Africa' },
  },
  'Select by Display Text': {
    textToEnter: { label: 'Option to select', placeholder: 'e.g. South Africa' },
  },
  'Select by Value': {
    textToEnter: { label: 'Option value', placeholder: "e.g. ZA (the option's value)" },
  },
  'Select by Position': {
    textToEnter: { label: 'Option position', hint: 'Counts from 1 — position 1 is the first option, 2 the second, and so on (not zero-based).', placeholder: 'e.g. 3 (the 3rd option)', type: 'number', min: 1 },
  },
  'Press Key': {
    keyCode: { label: 'Key to press' },
  },
  // Get Row Data — targetValue holds a 1-based row index (header excluded). Relabel
  // and show a number-only input; stored verbatim in targetValue — JSON unchanged.
  'Get Row Data': {
    targetValue: { label: 'Row Index', type: 'number', min: 1, hint: 'Counts from 1 — "1" is the first body row (header excluded).', placeholder: 'e.g. 2' },
  },
  // Click Element in Row — targetValue is the text/label of the element to click
  // within the matched row. Relabel only; stored verbatim in targetValue.
  'Click Element in Row': {
    targetValue: { label: 'Cell Text/Label', hint: 'Text or label of the element to click inside the matched row.', placeholder: "e.g. View" },
  },
  // Navigate Pagination — the choice (next/previous/first/last, or a 1-based page
  // number) is stored in `textToEnter`. Show a dropdown of the named controls that
  // still lets a page number be typed; relabel the field so it reads as the control.
  'Navigate Pagination': {
    textToEnter: { label: 'Control to Click', type: 'pagination', hint: 'Pick a control or type a page number', placeholder: 'e.g. next, or 3' },
  },
  // Switch to iFrame — same handler on iOS and Android, runs in a browser/WebView
  // context. Accepts EITHER the iframe element (web locator) OR a 1-based position;
  // relabel the generic fields so it's clear they're alternatives, not both required.
  'Switch to iFrame': {
    identifierType: { label: 'Find iFrame By', hint: 'Use a web locator — ID, Name, CSS or XPath. The iframe is a web element, so native strategies don’t apply. Leave blank if you’re using the position instead.' },
    locator:        { label: 'iFrame Element', hint: 'The iframe to step into. Leave blank to target it by position instead.', placeholder: 'e.g. my-frame' },
    textToEnter:    { label: 'Or iFrame Position', hint: 'Which iframe on the page, counting from 1 (1 = the first iframe). Use this instead of a locator.', placeholder: 'e.g. 1', type: 'number', min: 1 },
  },
  // Attribute actions — `targetValue` holds the attribute NAME to read, not a
  // second element. Relabel and show a platform-aware picker (Android UIAutomator2
  // vs iOS XCUITest attributes) instead of a free-text "Target Element" field.
  'Get Attribute': {
    targetValue: { label: 'Attribute Name', type: 'attribute', placeholder: 'Select or type an attribute…' },
  },
  'Verify Attribute': {
    targetValue: { label: 'Attribute Name', type: 'attribute', placeholder: 'Select or type an attribute…' },
  },
  'Set System Date Time': {
    systemDateTime: { type: 'datetime' },
    systemTimeZone: { type: 'timezone' },
  },
  // File uploads — the file path(s) are stored in `textToEnter`, but the generic
  // "Text" label reads wrong here. Relabel as a file-to-upload field (display only;
  // stored verbatim in textToEnter — JSON unchanged).
  'Upload File': {
    textToEnter: { label: 'File to Upload', hint: 'Path to the file to upload', placeholder: 'e.g. /sdcard/Download/document.pdf' },
  },
  'Upload Multiple Files': {
    textToEnter: { label: 'Files to Upload', hint: 'Comma-separated paths to the files to upload', placeholder: 'e.g. /sdcard/a.pdf, /sdcard/b.png' },
  },

  // --- Data & Math --- (mirrors WEB_UI_FIELD_OVERRIDES so the mobile editor shows
  // the same labels/hints/placeholders; rendered by the custom Data & Math fields.)
  'Apply Regex': {
    textToEnter:   { label: 'Input Value',    hint: 'The text to search in — use {{variable}} for extracted values', placeholder: 'e.g. {{pageText}}' },
    expectedValue: { label: 'Pattern',        hint: 'Java regex — wrap the part you want in (...); returns group 1 or the whole match if no groups', placeholder: 'e.g. Total (\\d+)' },
    variableName:  { label: 'Save match as',  hint: 'Reuse the matched value with {{variableName}}', placeholder: 'e.g. total' },
  },
  'Parse to Number': {
    textToEnter:  { label: 'Value',          hint: 'Must already be a bare number — use Apply Regex first if it contains text or formatting like $1,234', placeholder: 'e.g. {{total}}' },
    variableName: { label: 'Save number as', hint: 'Reuse the numeric value with {{variableName}}', placeholder: 'e.g. totalNum' },
  },
  'Calculate': {
    expectedValue: { label: 'Expression', hint: 'Use {{variables}}, operators + − × ÷ % ^, and functions: round, min, max, pow, sqrt, abs, ceil, floor', placeholder: 'e.g. round({{totalNum}} * 1.15, 2)' },
    variableName:  { label: 'Save result as', hint: 'Reuse the result with {{variableName}}', placeholder: 'e.g. withTax' },
  },
};

// Mobile actions with a fully custom Data & Math UI (no generic field renderer) —
// mirrors the Web UI's DATA_MATH_ACTIONS.
export const MOBILE_DATA_MATH_ACTIONS = new Set(['Apply Regex', 'Parse to Number', 'Calculate']);

// Per-action info notes shown as a small banner inside the action card (display only).
export const MOBILE_ACTION_INFO = {
  'Open URL': 'Opens a web page at this address. To jump to a screen inside the app, use “Open App Screen (Deep Link)”.',
  'Refresh Page': 'Reloads the current web page — for mobile browser sessions (websites) only, not native apps.',
  'Go Back': 'Goes back one step: in a browser session it returns to the previous page; in an Android native app it triggers the hardware/system back button. iOS native apps have no back button — tap the app’s on-screen back element instead.',
  'Go Forward': 'Navigates forward in the browser history — for mobile browser sessions (websites) only, not native apps.',
  'Trigger Deep Link': 'Opens a specific screen inside the app from its link (e.g. myapp://txn/123)',
  'Background App': 'Sends the app to the background and brings it back — for checking what happens on return, e.g. staying logged in or being logged out for security.',
  'Swipe': 'A fast flick across the whole screen that can fling past the edge. To move through a list or page, use Scroll instead.',
  'Swipe on Element': 'Flicks within one specific element (e.g. a carousel or slider), not the whole screen.',
  'Upload File': 'Uploads a file through a web upload form/field on the page (like choosing a file in a browser).',
  'Push File': 'Puts a file onto the device so the app can read it from storage.',
  'Set Media Library': 'Adds a photo to the device gallery so the app’s photo picker has one to choose.',
  'Send SMS': 'Simulates an SMS arriving on the device — e.g. to test an OTP being received and auto-filled.',
  'Grant Permissions': 'Pre-grants app permissions so the app doesn’t show a permission prompt. Android: only works if the app has declared the permission in its manifest and it’s a runtime (dangerous) permission. iOS: works for any installed app regardless of what it declared.',
  'Revoke Permissions': 'Removes permissions the app was previously granted. Android: only works if the app has declared the permission in its manifest and it’s a runtime (dangerous) permission. iOS: works for any installed app regardless of what it declared.',
  'Switch to iFrame': 'Steps into an iframe inside a web page so you can interact with the elements inside it. This runs in a browser/WebView context — on a native app, run “Switch to WebView” first. Target the iframe in one of two ways: by the iframe element (using a web locator like ID, Name, CSS or XPath) OR by its position on the page (1 = the first iframe) — provide one, not both. When you’re done, use “Switch to Parent Frame” or “Switch to Main Content” to get back out.',
  'Tap Notification': 'The text to enter is a snippet of what appears in the notification you’re trying to tap — a few distinctive words from its title or body are enough to find and open the right one.',
  'Dismiss Notification': 'The text to enter is a snippet of what appears in the notification you’re trying to dismiss — a few distinctive words from its title or body are enough to find and swipe away the right one.',
};

// iOS-only "show to user" notes — behavioural heads-ups (NOT errors) for actions
// that behave differently on iOS. Rendered as an extra info line only when the
// session device is iOS, so Android cards are left exactly as they are.
export const MOBILE_ACTION_IOS_NOTES = {
  'Set Locale': 'On iOS the app must be relaunched after changing the locale for the new language/region to take effect.',
  'Set System Date Time': 'On iOS this changes the on-screen clock. An app that computes its own dates/times may not reflect the change.',
};

// Platform / device constraints for mobile actions that can't run everywhere.
// Keyed by action. `allow` = the (platformName/connectionType) combos where it works;
// `warn` = combos where it usually fails but isn't a hard block. Any action NOT listed
// here works on any mobile session. connectionType is emulator (Android) | simulator (iOS) | physical.
export const MOBILE_ACTION_CONSTRAINTS = {
  'Send SMS':                { allow: ['Android/emulator'], reason: 'Only works on an Android emulator (injected via the emulator console) — not physical Android or iOS.' },
  'Set Network Conditions':  { allow: ['Android/emulator', 'Android/physical'], reason: 'Network state can only be driven on Android, not iOS.' },
  'Open Notifications':      { allow: ['Android/emulator', 'Android/physical'], reason: 'Uses the Android notification shade — iOS has no equivalent.' },
  'Tap Notification':        { allow: ['Android/emulator', 'Android/physical'], reason: 'Uses the Android notification shade — iOS has no equivalent.' },
  'Dismiss Notification':    { allow: ['Android/emulator', 'Android/physical'], reason: 'Uses the Android notification shade — iOS has no equivalent.' },
  'Get Current Activity':    { allow: ['Android/emulator', 'Android/physical'], reason: '“Current activity” is an Android-only concept.' },
  'Set Media Library':       { allow: ['Android/emulator', 'Android/physical', 'iOS/simulator'], reason: 'Not supported on a physical iOS device (Apple sandboxing).' },
  'Grant Permissions':       { allow: ['Android/emulator', 'Android/physical', 'iOS/simulator'], reason: 'Permissions can only be set on Android or the iOS simulator — not physical iOS.' },
  'Revoke Permissions':      { allow: ['Android/emulator', 'Android/physical', 'iOS/simulator'], reason: 'Permissions can only be set on Android or the iOS simulator — not physical iOS.' },
  'Set Locale':              { allow: ['Android/emulator', 'iOS/simulator'], warn: ['Android/physical'], reason: 'Uses simulator/emulator tooling; physical iOS is unsupported and physical Android is often blocked without root.' },
  'Set System Date Time':    { allow: ['Android/emulator', 'iOS/simulator'], warn: ['Android/physical'], reason: 'Needs a rooted emulator or the iOS simulator; physical devices are usually blocked.' },
};

/**
 * Evaluate whether a mobile action can run on a given device.
 * @returns {{ status: 'ok' | 'warn' | 'blocked', reason?: string }}
 */
export const evaluateMobileActionConstraint = (actionKey, device = {}) => {
  const c = MOBILE_ACTION_CONSTRAINTS[actionKey];
  if (!c) return { status: 'ok' };
  const platform = device.platform || 'Android';
  const connection = device.connection || (platform === 'iOS' ? 'simulator' : 'emulator');
  const combo = `${platform}/${connection}`;
  if (c.allow && c.allow.includes(combo)) return { status: 'ok' };
  if (c.warn && c.warn.includes(combo)) return { status: 'warn', reason: c.reason };
  return { status: 'blocked', reason: c.reason };
};

// Permissions for the mobile "Grant/Revoke Permissions" actions. The set shown
// depends on the (effective) device platform: Android uses fully-qualified manifest
// strings, iOS uses short simctl service names. Stored comma-separated in `permissions`.
export const MOBILE_PERMISSION_OPTIONS = {
  android: [
    { group: 'Camera & Microphone', keys: ['android.permission.CAMERA', 'android.permission.RECORD_AUDIO'] },
    { group: 'Location', keys: ['android.permission.ACCESS_FINE_LOCATION', 'android.permission.ACCESS_COARSE_LOCATION', 'android.permission.ACCESS_BACKGROUND_LOCATION'] },
    { group: 'Contacts', keys: ['android.permission.READ_CONTACTS', 'android.permission.WRITE_CONTACTS'] },
    { group: 'Calendar', keys: ['android.permission.READ_CALENDAR', 'android.permission.WRITE_CALENDAR'] },
    { group: 'SMS & Phone', keys: ['android.permission.READ_SMS', 'android.permission.SEND_SMS', 'android.permission.RECEIVE_SMS', 'android.permission.CALL_PHONE', 'android.permission.READ_PHONE_STATE'] },
    { group: 'Notifications (Android 13+)', keys: ['android.permission.POST_NOTIFICATIONS'] },
    { group: 'Media & Storage', keys: ['android.permission.READ_MEDIA_IMAGES', 'android.permission.READ_MEDIA_VIDEO', 'android.permission.READ_MEDIA_AUDIO', 'android.permission.READ_EXTERNAL_STORAGE', 'android.permission.WRITE_EXTERNAL_STORAGE'] },
    { group: 'Sensors', keys: ['android.permission.BODY_SENSORS'] },
  ],
  // iOS service names are passed verbatim to the driver: most go through
  // applesimutils, location/location-always go through simctl. (Simulator only —
  // the driver's setPermission throws on physical iOS, gated by the action constraint.)
  // This is the closed set the engine accepts on iOS — names outside it fail the run,
  // so the picker only offers these (unlike Android's open-ended manifest strings).
  ios: [
    { group: 'Camera & Microphone', keys: ['camera', 'microphone'] },
    { group: 'Location (via simctl)', keys: ['location', 'location-always'] },
    { group: 'Personal Data', keys: ['photos', 'medialibrary', 'contacts', 'calendar', 'reminders', 'health'] },
    { group: 'Motion', keys: ['motion'] },
    { group: 'Voice & System', keys: ['siri', 'speech', 'homekit'] },
  ],
};

// Attribute options for the mobile "Get Attribute" / "Verify Attribute" actions.
// The set shown depends on the (effective) device platform — UIAutomator2 (Android)
// and XCUITest (iOS) expose different attribute names. Stored verbatim in `targetValue`.
// The picker allows a custom value too, so anything not listed can still be typed.
export const MOBILE_ATTRIBUTE_OPTIONS = {
  android: [
    { value: 'text',           label: 'text',           hint: 'Visible text of the element' },
    { value: 'content-desc',   label: 'content-desc',   hint: 'Accessibility description' },
    { value: 'resource-id',    label: 'resource-id',    hint: "Element's resource ID" },
    { value: 'class',          label: 'class',          hint: 'UI class name (e.g. android.widget.EditText)' },
    { value: 'package',        label: 'package',        hint: 'App package name' },
    { value: 'enabled',        label: 'enabled',        hint: '"true" / "false"' },
    { value: 'checked',        label: 'checked',        hint: '"true" / "false" (checkboxes, toggles)' },
    { value: 'selected',       label: 'selected',       hint: '"true" / "false"' },
    { value: 'clickable',      label: 'clickable',      hint: '"true" / "false"' },
    { value: 'focusable',      label: 'focusable',      hint: '"true" / "false"' },
    { value: 'focused',        label: 'focused',        hint: '"true" / "false"' },
    { value: 'scrollable',     label: 'scrollable',     hint: '"true" / "false"' },
    { value: 'long-clickable', label: 'long-clickable', hint: '"true" / "false"' },
    { value: 'password',       label: 'password',       hint: '"true" / "false" (is it a password field)' },
    { value: 'displayed',      label: 'displayed',      hint: '"true" / "false"' },
    { value: 'bounds',         label: 'bounds',         hint: 'Element bounds e.g. [0,100][540,200]' },
    { value: 'index',          label: 'index',          hint: 'Index among siblings' },
  ],
  ios: [
    { value: 'value',      label: 'value',      hint: 'Current value (text fields, sliders, toggles)' },
    { value: 'label',      label: 'label',      hint: 'Accessibility label' },
    { value: 'name',       label: 'name',       hint: 'Accessibility identifier' },
    { value: 'type',       label: 'type',       hint: 'XCUIElement type (e.g. XCUIElementTypeTextField)' },
    { value: 'enabled',    label: 'enabled',    hint: '"true" / "false"' },
    { value: 'visible',    label: 'visible',    hint: '"true" / "false"' },
    { value: 'accessible', label: 'accessible', hint: '"true" / "false"' },
    { value: 'selected',   label: 'selected',   hint: '"true" / "false"' },
    { value: 'focused',    label: 'focused',    hint: '"true" / "false"' },
    { value: 'hittable',   label: 'hittable',   hint: '"true" / "false" (can be tapped)' },
    { value: 'rect',       label: 'rect',       hint: 'JSON with x, y, width, height' },
    { value: 'index',      label: 'index',      hint: 'Index among siblings' },
  ],
};

// Accepted keys for the mobile "Press Key" action, grouped for a searchable picker.
// The selected name is stored verbatim in `keyCode` (JSON unchanged). Groups are
// labelled by where they apply (web keys, iOS hardware buttons, Android keycodes).
export const MOBILE_KEY_GROUPS = [
  { group: 'Browser keys (Android / iOS web)', keys: ['ENTER', 'TAB', 'BACKSPACE', 'ESCAPE', 'SPACE', 'DELETE', 'ARROW_UP', 'ARROW_DOWN'] },
  { group: 'iOS — hardware buttons', keys: ['home', 'volumeUp', 'volumeDown'] },
  { group: 'Android — Common / navigation', keys: ['HOME', 'BACK', 'MENU', 'SEARCH', 'APP_SWITCH', 'NOTIFICATION', 'POWER', 'CAMERA', 'CALL', 'ENDCALL', 'ENTER', 'TAB', 'SPACE', 'DEL', 'FORWARD_DEL', 'ESCAPE', 'CLEAR', 'INSERT', 'SYSRQ', 'BREAK', 'MOVE_HOME', 'MOVE_END', 'PAGE_UP', 'PAGE_DOWN', 'FORWARD', 'SLEEP', 'WAKEUP', 'HELP', 'ASSIST', 'VOICE_ASSIST'] },
  { group: 'Android — D-pad', keys: ['DPAD_UP', 'DPAD_DOWN', 'DPAD_LEFT', 'DPAD_RIGHT', 'DPAD_CENTER', 'DPAD_UP_LEFT', 'DPAD_UP_RIGHT', 'DPAD_DOWN_LEFT', 'DPAD_DOWN_RIGHT'] },
  { group: 'Android — Letters / digits / symbols', keys: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'DIGIT_0', 'DIGIT_1', 'DIGIT_2', 'DIGIT_3', 'DIGIT_4', 'DIGIT_5', 'DIGIT_6', 'DIGIT_7', 'DIGIT_8', 'DIGIT_9', 'STAR', 'POUND', 'COMMA', 'PERIOD', 'MINUS', 'EQUALS', 'PLUS', 'AT', 'GRAVE', 'APOSTROPHE', 'SEMICOLON', 'SLASH', 'BACKSLASH', 'LEFT_BRACKET', 'RIGHT_BRACKET'] },
  { group: 'Android — Modifiers', keys: ['SHIFT_LEFT', 'SHIFT_RIGHT', 'ALT_LEFT', 'ALT_RIGHT', 'CTRL_LEFT', 'CTRL_RIGHT', 'META_LEFT', 'META_RIGHT', 'CAPS_LOCK', 'NUM_LOCK', 'SCROLL_LOCK', 'FUNCTION', 'SYM', 'NUM'] },
  { group: 'Android — Numpad', keys: ['NUMPAD_0', 'NUMPAD_1', 'NUMPAD_2', 'NUMPAD_3', 'NUMPAD_4', 'NUMPAD_5', 'NUMPAD_6', 'NUMPAD_7', 'NUMPAD_8', 'NUMPAD_9', 'NUMPAD_DIVIDE', 'NUMPAD_MULTIPLY', 'NUMPAD_SUBTRACT', 'NUMPAD_ADD', 'NUMPAD_DOT', 'NUMPAD_COMMA', 'NUMPAD_ENTER', 'NUMPAD_EQUALS', 'NUMPAD_LEFT_PAREN', 'NUMPAD_RIGHT_PAREN'] },
  { group: 'Android — Function', keys: ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'] },
  { group: 'Android — Volume / media', keys: ['VOLUME_UP', 'VOLUME_DOWN', 'VOLUME_MUTE', 'MUTE', 'MEDIA_PLAY', 'MEDIA_PAUSE', 'MEDIA_PLAY_PAUSE', 'MEDIA_STOP', 'MEDIA_NEXT', 'MEDIA_PREVIOUS', 'MEDIA_REWIND', 'MEDIA_FAST_FORWARD', 'MEDIA_RECORD', 'MEDIA_EJECT', 'MEDIA_CLOSE', 'MEDIA_SKIP_FORWARD', 'MEDIA_SKIP_BACKWARD', 'MEDIA_STEP_FORWARD', 'MEDIA_STEP_BACKWARD', 'MEDIA_AUDIO_TRACK'] },
  { group: 'Android — Editing', keys: ['CUT', 'COPY', 'PASTE'] },
  { group: 'Android — Gamepad', keys: ['BUTTON_A', 'BUTTON_B', 'BUTTON_C', 'BUTTON_X', 'BUTTON_Y', 'BUTTON_Z', 'BUTTON_L1', 'BUTTON_R1', 'BUTTON_L2', 'BUTTON_R2', 'BUTTON_THUMBL', 'BUTTON_THUMBR', 'BUTTON_START', 'BUTTON_SELECT', 'BUTTON_MODE', 'BUTTON_1', 'BUTTON_2', 'BUTTON_3', 'BUTTON_4', 'BUTTON_5', 'BUTTON_6', 'BUTTON_7', 'BUTTON_8', 'BUTTON_9', 'BUTTON_10', 'BUTTON_11', 'BUTTON_12', 'BUTTON_13', 'BUTTON_14', 'BUTTON_15', 'BUTTON_16'] },
  { group: 'Android — Apps / system', keys: ['CONTACTS', 'CALENDAR', 'MUSIC', 'CALCULATOR', 'EXPLORER', 'ENVELOPE', 'SETTINGS', 'BOOKMARK', 'BRIGHTNESS_UP', 'BRIGHTNESS_DOWN', 'ZOOM_IN', 'ZOOM_OUT', 'HEADSETHOOK', 'FOCUS', 'PAIRING', 'NAVIGATE_PREVIOUS', 'NAVIGATE_NEXT', 'NAVIGATE_IN', 'NAVIGATE_OUT', 'LANGUAGE_SWITCH', 'MANNER_MODE', 'MODE_3D'] },
  { group: 'Android — TV / set-top (rarely needed)', keys: ['TV', 'WINDOW', 'GUIDE', 'DVR', 'CAPTIONS', 'CHANNEL_UP', 'CHANNEL_DOWN', 'INFO', 'TV_POWER', 'TV_INPUT', 'STB_POWER', 'STB_INPUT', 'AVR_POWER', 'AVR_INPUT', 'PROG_RED', 'PROG_GREEN', 'PROG_YELLOW', 'PROG_BLUE', 'STEM_PRIMARY', 'STEM_1', 'STEM_2', 'STEM_3', 'TV_INPUT_HDMI_1', 'TV_INPUT_HDMI_2', 'TV_INPUT_HDMI_3', 'TV_INPUT_HDMI_4', 'TV_INPUT_COMPOSITE_1', 'TV_INPUT_COMPOSITE_2', 'TV_INPUT_COMPONENT_1', 'TV_INPUT_COMPONENT_2', 'TV_INPUT_VGA_1', 'TV_DATA_SERVICE', 'TV_RADIO_SERVICE', 'TV_TELETEXT', 'TV_NUMBER_ENTRY', 'TV_NETWORK', 'TV_ANTENNA_CABLE', 'TV_TERRESTRIAL_ANALOG', 'TV_TERRESTRIAL_DIGITAL', 'TV_SATELLITE', 'TV_SATELLITE_BS', 'TV_SATELLITE_CS', 'TV_SATELLITE_SERVICE', 'TV_AUDIO_DESCRIPTION', 'TV_AUDIO_DESCRIPTION_MIX_UP', 'TV_AUDIO_DESCRIPTION_MIX_DOWN', 'TV_ZOOM_MODE', 'TV_CONTENTS_MENU', 'TV_MEDIA_CONTEXT_MENU', 'TV_TIMER_PROGRAMMING'] },
  { group: 'Android — Japanese IME / misc', keys: ['ZENKAKU_HANKAKU', 'EISU', 'MUHENKAN', 'HENKAN', 'KATAKANA_HIRAGANA', 'YEN', 'RO', 'KANA', 'SOFT_LEFT', 'SOFT_RIGHT', 'SOFT_SLEEP', 'PICTSYMBOLS', 'SWITCH_CHARSET', 'MEDIA_TOP_MENU', 'KEY_11', 'KEY_12', 'LAST_CHANNEL', 'UNKNOWN'] },
];

// Field labels for mobile actions
export const MOBILE_FIELD_LABELS = {
  identifierType:           'Find By',
  locator:                  'Target Element',
  textToEnter:              'Text',
  expectedValue:            'Expected Value',
  actualValue:              'Actual Value',
  targetValue:              'Target Element',
  variableName:             'Save As Variable',
  timeout:                  'Wait Up To',
  pressDurationMs:          'Press for how long',
  additionalValue:          'Column Name',
  searchColumnName:         'Search Column',
  searchValue:              'Search Value',
  columnName:               'Column Name',
  columnValue:              'Column Value',
  elementType:              'Element Type',
  filterValue:              'Filter Value',
  paginationAction:         'Page Action',
  swipeDirection:           'Direction',
  scrollDirection:          'Direction',
  swipeSpeed:               'Speed',
  swipeDurationMs:          'Duration',
  pinchScale:               'Scale',
  keyCode:                  'Key Code',
  url:                      'URL',
  orientation:              'Orientation',
  geoLatitude:              'Latitude',
  geoLongitude:             'Longitude',
  geoAltitude:              'Altitude',
  networkCondition:         'Network Condition',
  localeLanguage:           'Language',
  localeCountry:            'Country',
  systemDateTime:           'Date / Time',
  systemTimeZone:           'Time Zone',
  smsMessage:               'Message',
  smsFromNumber:            'From Number',
  mediaFileName:            'Media File',
  mediaContentBase64:       'Content (Base64)',
  mediaMimeType:            'MIME Type',
  remoteFilePath:           'Where to save it on the device',
  sourceFile:               'File to upload',
  notificationTitle:        'Notification Title',
  notificationBody:         'Notification Body',
  clipboardContent:         'Clipboard Content',
  appPath:                  'App Path',
  appPackage:               'App Package',
  appActivity:              'App Activity',
  bundleId:                 'Bundle ID',
  backgroundDurationSeconds:'Duration (seconds)',
  scriptCommand:            'Script',
  scriptArgs:               'Arguments',
  contextName:              'Context Name',
  permissions:              'Permissions',
  permissionAction:         'Permission Action',
  retryCount:               'Retry Count',
};

// Pre-grouped mobile actions by category for the action picker dropdown
export const MOBILE_ACTIONS_BY_CATEGORY = Object.entries(MOBILE_ACTIONS).reduce((acc, [key, action]) => {
  if (!acc[action.category]) acc[action.category] = [];
  acc[action.category].push({ value: key, ...action });
  return acc;
}, {});

// Appium element location strategies
export const MOBILE_IDENTIFIER_TYPES = [
  { value: 'accessibilityId',   label: 'Accessibility ID',       hint: 'Android content-desc / iOS accessibility id — best cross-platform' },
  { value: 'id',                label: 'ID',                     hint: 'Resource id — e.g. com.app:id/login' },
  { value: 'xpath',             label: 'XPath',                  hint: 'Element-tree XPath — works everywhere, slower' },
  { value: 'className',         label: 'Class Name',             hint: 'e.g. android.widget.Button / XCUIElementTypeButton' },
  { value: 'name',              label: 'Name',                   hint: 'Element name attribute' },
  { value: 'image',             label: 'Image',                  hint: 'Match by a reference image' },
  { value: 'uiAutomator',       label: 'UIAutomator',            hint: 'UiSelector strings — e.g. new UiSelector().text("OK")' },
  { value: 'iosPredicate',      label: 'iOS Predicate',          hint: 'NSPredicate string — iOS only' },
  { value: 'iosClassChain',     label: 'iOS Class Chain',        hint: 'iOS class chain — iOS only' },
  { value: 'cssSelector',       label: 'CSS Selector',           hint: 'CSS selector — WebView / browser contexts' },
  { value: 'tagName',           label: 'Tag Name',               hint: 'HTML tag name — WebView / browser contexts' },
  { value: 'linkText',          label: 'Link Text',              hint: 'Exact anchor link text — WebView / browser contexts' },
  { value: 'partialLinkText',   label: 'Partial Link Text',      hint: 'Partial anchor link text — WebView / browser contexts' },
];

export const MOBILE_PERMISSION_ACTIONS = [
  { value: 'allow',   label: 'Allow',   hint: 'Grant the permission' },
  { value: 'deny',    label: 'Deny',    hint: 'Deny the permission' },
  { value: 'dismiss', label: 'Dismiss', hint: 'Dismiss without choosing' },
];

// ===== Mobile Control Flow (Conditional / Loop / ForEach) =====
// The mobile engine evaluates the SAME condition set as the Web-UI engine
// (MobileTestEngine.evaluateCondition mirrors the web ConditionEvaluator), so the
// catalogue is web's list verbatim plus the mobile-only Appium context check.
// Deriving it from CONDITION_TYPES rather than restating it keeps the two pickers
// from drifting: a type added for web is offered on mobile automatically.
export const MOBILE_CONDITION_TYPES = [
  ...CONDITION_TYPES,
  {
    value: 'contextEquals',
    label: 'Current Context Equals',
    fields: 'context',
    hint: 'Current Appium context — e.g. NATIVE_APP or WEBVIEW_…',
  },
];

// Types that only mean something in a WebView / mobile-web context. On a native
// screen the engine degrades them safely rather than erroring — URL and title
// resolve to an empty string (so *Equals / *Contains are false) and there is no
// CSS class to read — so they are offered everywhere but flagged in the editor.
export const MOBILE_WEBVIEW_ONLY_CONDITIONS = new Set([
  'urlEquals', 'urlContains', 'titleEquals', 'titleContains', 'hasClass', 'notHasClass',
]);

// inputValue* reads the `value` attribute in a WebView and falls back to the
// element's visible text on native — worth saying out loud in the editor.
export const MOBILE_NATIVE_FALLBACK_CONDITIONS = new Set([
  'inputValueEquals', 'inputValueContains',
]);

// Grouped for the mobile condition picker — same grouping the Web-UI dropdown uses,
// with the mobile-only Context group appended.
export const MOBILE_CONDITION_TYPE_GROUPS = [
  { label: 'Element Conditions',        types: MOBILE_CONDITION_TYPES.filter((ct) => ct.fields === 'element') },
  { label: 'Text & Content Conditions', types: MOBILE_CONDITION_TYPES.filter((ct) => ct.fields === 'text') },
  { label: 'Attribute Conditions',      types: MOBILE_CONDITION_TYPES.filter((ct) => ct.fields === 'attribute') },
  { label: 'Count Conditions',          types: MOBILE_CONDITION_TYPES.filter((ct) => ct.fields === 'count') },
  { label: 'Page Conditions',           types: MOBILE_CONDITION_TYPES.filter((ct) => ct.fields === 'page') },
  { label: 'Alert Conditions',          types: MOBILE_CONDITION_TYPES.filter((ct) => ct.fields === 'alert') },
  { label: 'Variable Conditions',       types: MOBILE_CONDITION_TYPES.filter((ct) => ct.fields === 'value') },
  { label: 'Action Result Conditions',  types: MOBILE_CONDITION_TYPES.filter((ct) => ct.fields === 'actionRef') },
  { label: 'Context Conditions',        types: MOBILE_CONDITION_TYPES.filter((ct) => ct.fields === 'context') },
];

// Map of condition type → its field category (element | text | attribute | count |
// page | alert | value | actionRef | context). Single source of truth used by the
// editor to decide which inputs to render and by the serializer to keep only the
// fields that type actually uses.
export const MOBILE_CONDITION_TYPE_FIELDS = Object.fromEntries(
  MOBILE_CONDITION_TYPES.map((ct) => [ct.value, ct.fields])
);

// Legacy loop types, kept only so payloads written before the patterned-loop engine
// landed can be migrated on load (see migrateLegacyMobileLoop). Never authored now.
export const MOBILE_LEGACY_LOOP_TYPES = ['WHILE', 'UNTIL'];

// Mobile scripts saved before the shared condition schema landed used SCREAMING_CASE
// type names. Migration table applied on hydrate so those scripts keep working and
// re-save in the canonical camelCase spelling.
export const MOBILE_LEGACY_CONDITION_TYPES = {
  ELEMENT_EXISTS:     'elementExists',
  ELEMENT_NOT_EXISTS: 'elementNotExists',
  ELEMENT_VISIBLE:    'elementVisible',
  VALUE_EQUALS:       'valueEquals',
  VALUE_NOT_EQUALS:   'valueNotEquals',
  VALUE_CONTAINS:     'valueContains',
  ACTION_PASSED:      'actionPassed',
  ACTION_FAILED:      'actionFailed',
  CONTEXT_EQUALS:     'contextEquals',
};

// Resolve any stored condition type to its canonical camelCase form.
export const normalizeMobileConditionType = (type) =>
  (type ? (MOBILE_LEGACY_CONDITION_TYPES[type] || type) : '');

// Negations within the mobile condition set, used when migrating a legacy WHILE loop
// ("repeat while true") to a patterned poll loop ("stop when true"). Types absent
// from this map have no single-type opposite, so those migrations are flagged for
// the user to confirm instead of being inverted incorrectly.
export const MOBILE_CONDITION_NEGATIONS = {
  elementExists:      'elementNotExists',
  elementNotExists:   'elementExists',
  elementVisible:     'elementNotVisible',
  elementNotVisible:  'elementVisible',
  elementEnabled:     'elementDisabled',
  elementDisabled:    'elementEnabled',
  elementSelected:    'elementNotSelected',
  elementNotSelected: 'elementSelected',
  hasClass:           'notHasClass',
  notHasClass:        'hasClass',
  valueEquals:        'valueNotEquals',
  valueNotEquals:     'valueEquals',
  actionPassed:       'actionFailed',
  actionFailed:       'actionPassed',
};

// Common context values for the contextEquals condition (free text still allowed).
export const MOBILE_CONTEXT_OPTIONS = [
  { value: 'NATIVE_APP', label: 'NATIVE_APP' },
  { value: 'WEBVIEW',    label: 'WEBVIEW' },
];

// v2 patterned-loop modes — same four values the Web-UI engine takes, with the
// gesture wording adjusted for mobile (scroll = swipe page-down, not page scroll).
export const MOBILE_LOOP_PATTERNS = [
  { value: 'retry',  label: 'Retry',  hint: 'Run body → check condition → retry on false' },
  { value: 'poll',   label: 'Poll',   hint: 'Check condition first → stop on true, else run body' },
  { value: 'scroll', label: 'Scroll', hint: 'Swipe down each iteration, optionally toward an element' },
  { value: 'custom', label: 'Custom', hint: 'Same as Poll — manual control-flow label' },
];

// Hard caps enforced by the backend — surfaced in the editor so users aren't surprised.
export const MOBILE_LOOP_MAX_ITERATIONS = 50;
export const MOBILE_FOREACH_MAX_ITEMS = 100;

// Blank condition object shared by Conditional branches and loop.stopCondition.
// Same keys as blankWebUiCondition (including `attribute`) — one shared schema.
export const blankMobileCondition = () => ({
  type: '',
  identifierType: 'accessibilityId',
  locator: '',
  variable: '',
  expectedValue: '',
  attribute: '',
  actionRef: '',
  timeout: 3000,
});

export const MOBILE_SWIPE_DIRECTIONS = [
  { value: 'up',    label: 'Up' },
  { value: 'down',  label: 'Down' },
  { value: 'left',  label: 'To the left' },
  { value: 'right', label: 'To the right' },
];

export const MOBILE_ORIENTATIONS = [
  { value: 'PORTRAIT',  label: 'Portrait' },
  { value: 'LANDSCAPE', label: 'Landscape' },
];

export const MOBILE_PAGINATION_ACTIONS = [
  { value: 'next',  label: 'Next Page' },
  { value: 'prev',  label: 'Previous Page' },
  { value: 'first', label: 'First Page' },
  { value: 'last',  label: 'Last Page' },
];

// Pagination controls for the mobile "Navigate Pagination" action. The choice is
// stored in `textToEnter` — users pick a named control here or type a 1-based page
// number instead (handled via the dropdown's allow-custom path).
export const MOBILE_PAGINATION_CONTROLS = [
  { value: 'next',     label: 'Next' },
  { value: 'previous', label: 'Previous' },
  { value: 'first',    label: 'First' },
  { value: 'last',     label: 'Last' },
];

export const MOBILE_NETWORK_CONDITIONS = [
  { value: 'ALL',         label: 'All — fully connected (restore)' },
  { value: 'WIFI_ONLY',   label: 'Wi-Fi only' },
  { value: 'MOBILE_ONLY', label: 'Mobile data only' },
  { value: 'OFFLINE',     label: 'Offline — no connectivity (not airplane mode)' },
  { value: 'AIRPLANE',    label: 'Airplane mode' },
];

export const MOBILE_LOCALE_LANGUAGES = [
  { value: 'en', label: 'English (en)' },
  { value: 'af', label: 'Afrikaans (af)' },
  { value: 'zu', label: 'Zulu (zu)' },
  { value: 'xh', label: 'Xhosa (xh)' },
  { value: 'fr', label: 'French (fr)' },
  { value: 'es', label: 'Spanish (es)' },
  { value: 'pt', label: 'Portuguese (pt)' },
  { value: 'de', label: 'German (de)' },
  { value: 'it', label: 'Italian (it)' },
  { value: 'nl', label: 'Dutch (nl)' },
  { value: 'ar', label: 'Arabic (ar)' },
  { value: 'zh', label: 'Chinese (zh)' },
  { value: 'ja', label: 'Japanese (ja)' },
  { value: 'ko', label: 'Korean (ko)' },
  { value: 'hi', label: 'Hindi (hi)' },
  { value: 'ru', label: 'Russian (ru)' },
];

export const MOBILE_LOCALE_COUNTRIES = [
  { value: 'ZA', label: 'South Africa (ZA)' },
  { value: 'US', label: 'United States (US)' },
  { value: 'GB', label: 'United Kingdom (GB)' },
  { value: 'CA', label: 'Canada (CA)' },
  { value: 'AU', label: 'Australia (AU)' },
  { value: 'IN', label: 'India (IN)' },
  { value: 'FR', label: 'France (FR)' },
  { value: 'DE', label: 'Germany (DE)' },
  { value: 'ES', label: 'Spain (ES)' },
  { value: 'PT', label: 'Portugal (PT)' },
  { value: 'BR', label: 'Brazil (BR)' },
  { value: 'NL', label: 'Netherlands (NL)' },
  { value: 'CN', label: 'China (CN)' },
  { value: 'JP', label: 'Japan (JP)' },
  { value: 'KR', label: 'South Korea (KR)' },
  { value: 'AE', label: 'United Arab Emirates (AE)' },
];

export const MOBILE_FIELD_PLACEHOLDERS = {
  locator:                   '//android.widget.Button[@text="OK"]',
  textToEnter:               'Text to type or {{variable}}',
  expectedValue:             'Expected value or {{variable}}',
  actualValue:               '{{variable}}',
  variableName:              'myResultVar',
  url:                       'https://example.com',
  keyCode:                   'KEYCODE_BACK or 4',
  timeout:                   '5000',
  pressDurationMs:           'e.g. 3000',
  geoLatitude:               '-33.8688',
  geoLongitude:              '151.2093',
  geoAltitude:               '50',
  localeLanguage:            'en',
  localeCountry:             'US',
  systemDateTime:            '2024-01-15T14:30:00',
  systemTimeZone:            'America/New_York',
  smsMessage:                'SMS message body',
  smsFromNumber:             '+1234567890',
  mediaFileName:             'test_image.jpg',
  remoteFilePath:            '/sdcard/Download/statement.pdf',
  sourceFile:                '{{test_files.statement.pdf}}',
  notificationTitle:         'Notification title',
  notificationBody:          'Notification message',
  clipboardContent:          'Text to copy to clipboard',
  appPath:                   '/path/to/app.apk',
  appPackage:                'com.android.settings',
  appActivity:               '.Settings',
  bundleId:                  'com.apple.Preferences',
  contextName:               'WEBVIEW_com.app (blank = auto)',
  scriptCommand:             'mobile: scroll',
  scriptArgs:                '{"direction": "down"}',
  filterValue:               'Filter value',
  swipeSpeed:                '500',
  swipeDurationMs:           '1000',
  pinchScale:                '2.0',
  backgroundDurationSeconds: '3',
  columnName:                'Column name',
  columnValue:               'Column value',
  searchColumnName:          'Column to search in',
  targetValue:               'Target element locator',
  additionalValue:           'Column name',
  description:               'Optional description',
};

export const createDefaultMobileAction = (actionType = '') => {
  const base = {
  id:                        `mob_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  actionType,
  description:               '',
  identifierType:            'accessibilityId',
  locator:                   '',
  textToEnter:               '',
  expectedValue:             '',
  actualValue:               '',
  variableName:              '',
  targetValue:               '',
  additionalValue:           '',
  columnName:                '',
  columnValue:               '',
  searchColumnName:          '',
  elementType:               '',
  filterValue:               '',
  paginationAction:          '',
  timeout:                   '',
  pressDurationMs:           3000,
  swipeDirection:            'down',
  scrollDirection:           'down',
  swipeDurationMs:           '',
  pinchScale:                '',
  retryCount:                '',
  keyCode:                   '',
  url:                       '',
  orientation:               'PORTRAIT',
  geoLatitude:               '',
  geoLongitude:              '',
  geoAltitude:               0,
  networkCondition:          '',
  localeLanguage:            '',
  localeCountry:             '',
  systemDateTime:            '',
  systemTimeZone:            '',
  smsMessage:                '',
  smsFromNumber:             '',
  mediaFileName:             '',
  mediaContentBase64:        '',
  mediaMimeType:             '',
  remoteFilePath:            '',
  sourceFile:                '',
  notificationTitle:         '',
  notificationBody:          '',
  clipboardContent:          '',
  appPath:                   '',
  appPackage:                '',
  appActivity:               '',
  bundleId:                  '',
  backgroundDurationSeconds: '',
  scriptCommand:             '',
  scriptArgs:                '',
  contextName:               '',
  permissions:               '',
  permissionAction:          '',
  };

  // Control-flow actions carry nested structures the backend reads. They are
  // serialized with a bespoke shape (see stripActionForStorage), not the flat
  // required/optional field list used by ordinary actions. Conditional and Loop use
  // the same v2 shapes as the Web-UI engine (branches[] / loop.pattern) — one shared
  // JSON contract across both platforms.
  if (actionType === 'Conditional') {
    base.screenshotsPerIteration = 'all';
    base.branches = [
      {
        id: `mob-br-${Date.now()}-1`,
        type: 'if',
        condition: blankMobileCondition(),
        actions: [],
      },
    ];
  } else if (actionType === 'Loop') {
    base.screenshotsPerIteration = 'all';
    base.loop = {
      pattern: 'retry',
      stopCondition: blankMobileCondition(),
      maxIterations: 10,
      delayMs: 0,
    };
    base.actions = [];
  } else if (actionType === 'ForEach') {
    // ForEach is NOT part of the shared v2 contract — the mobile engine still reads
    // identifierType / locator directly off the action (serialized as the canonical
    // "For Each Element"), so its payload stays flat while its editor UI matches web.
    base.identifierType = 'xpath';
    base.locator = '';
    base.actions = [];
  }
  // Stop & Exit actions (Break Loop / End Permutation) need no extra shape — they
  // take no fields, so the flat base (action + description) is the whole action.

  return base;
};

// ===== API Assertion Constants =====

// Standard assertion paths for API steps (response + request metadata)
export const COMMON_ASSERTION_PATHS = [
  { path: 'response.statusCode', label: 'statusCode', source: 'Response' },
  { path: 'response.content-type', label: 'content-type', source: 'Response' },
  { path: 'response.authorization', label: 'authorization', source: 'Response' },
  { path: 'response.cache-control', label: 'cache-control', source: 'Response' },
  { path: 'response.set-cookie', label: 'set-cookie', source: 'Response' },
  { path: 'response.responseTime', label: 'responseTime', source: 'Response' },
  { path: 'response.size', label: 'size', source: 'Response' },
  { path: 'response.redirectCount', label: 'redirectCount', source: 'Response' },
  { path: 'response.url', label: 'url', source: 'Response' },
  { path: 'request.content-type', label: 'content-type', source: 'Request' },
  { path: 'request.authorization', label: 'authorization', source: 'Request' },
  { path: 'request.method', label: 'method', source: 'Request' },
  { path: 'request.url', label: 'url', source: 'Request' },
];

// Assertion operators
export const ASSERTION_OPERATORS = [
  { value: 'EQUALS', label: 'EQUALS' },
  { value: 'NOT_EQUALS', label: 'NOT_EQUALS' },
  { value: 'CONTAINS', label: 'CONTAINS' },
  { value: 'NOT_CONTAINS', label: 'NOT_CONTAINS' },
  { value: 'GREATER_THAN', label: 'GREATER_THAN' },
  { value: 'LESS_THAN', label: 'LESS_THAN' },
  { value: 'NULL', label: 'NULL' },
  { value: 'NOT_NULL', label: 'NOT_NULL' },
  { value: 'TRUE', label: 'TRUE' },
  { value: 'FALSE', label: 'FALSE' },
  { value: 'MATCHES', label: 'MATCHES' },
  { value: 'STARTS_WITH', label: 'STARTS_WITH' },
  { value: 'ENDS_WITH', label: 'ENDS_WITH' },
];

// ===== Database Step Constants =====
//demo
export const DATABASE_TYPE_CONFIG = {
  PostgreSQL: { label: 'PostgreSQL', color: '#336791', defaultPort: '5432', defaultSchema: 'public' },
  MySQL:      { label: 'MySQL',      color: '#4479A1', defaultPort: '3306', defaultSchema: '' },
  Oracle:     { label: 'Oracle',     color: '#F80000', defaultPort: '1521', defaultSchema: '' },
  SQLServer:  { label: 'SQL Server', color: '#CC2927', defaultPort: '1433', defaultSchema: 'dbo' },
};

export const COMMON_DB_ASSERTION_PATHS = [
  { path: 'result.rowCount', label: 'rowCount', source: 'Result' },
  { path: 'result.rows_affected', label: 'rows_affected', source: 'Result' },
  { path: 'result.execution_time_ms', label: 'execution_time_ms', source: 'Result' },
  { path: 'result.success', label: 'success', source: 'Result' },
  { path: 'result.error', label: 'error', source: 'Result' },
];

// ===== Reserved / system-generated file names =====
const RESERVED_FILE_NAMES = new Set([
  'request_payload.json',
  'request_data_global_data_source.csv',
  'response_data_global_data_source.csv',
  'environments.csv',
]);

export const isReservedFileName = (name) => {
  const lower = (name || '').toLowerCase();
  if (RESERVED_FILE_NAMES.has(lower)) return true;
  if (lower.endsWith('_report.html')) return true;
  return false;
};

export const getUniqueFileName = (name, existingNames) => {
  if (!existingNames.has(name)) return name;
  const dotIdx = name.lastIndexOf('.');
  const stem = dotIdx > 0 ? name.slice(0, dotIdx) : name;
  const ext = dotIdx > 0 ? name.slice(dotIdx) : '';
  let counter = 1;
  let candidate = `${stem}_(${counter})${ext}`;
  while (existingNames.has(candidate)) {
    counter++;
    candidate = `${stem}_(${counter})${ext}`;
  }
  return candidate;
};

// ===== v2 Control-Flow Constants =====

// v2 Loop patterns (replaces legacy loop.type: while|until)
export const LOOP_PATTERNS = [
  { value: 'retry',  label: 'Retry',  hint: 'Run body → check condition → retry on false' },
  { value: 'poll',   label: 'Poll',   hint: 'Check condition first → stop on true, else run body' },
  { value: 'scroll', label: 'Scroll', hint: 'Scroll page / element each iteration' },
  { value: 'custom', label: 'Custom', hint: 'Same as Poll — manual control-flow label' },
];

// Screenshot capture policy inside loop / ForEach / Conditional bodies
export const SCREENSHOTS_PER_ITERATION_OPTIONS = [
  { value: 'all',        label: 'All sub-steps (default)' },
  { value: 'perElement', label: 'Once per iteration' },
  { value: 'off',        label: 'Off — no screenshots inside body' },
];

// ForEach stop-when modes
export const FOREACH_STOP_WHEN_OPTIONS = [
  { value: 'fixed',     label: 'Fixed count (maxItems)',          hint: 'Process up to maxItems matched elements' },
  { value: 'condition', label: 'Stop on condition',               hint: 'Stop when stopCondition becomes true' },
];

// Branch type options for v2 Conditional
export const BRANCH_TYPE_OPTIONS = [
  { value: 'if',     label: 'IF' },
  { value: 'elseif', label: 'ELSE IF' },
  { value: 'else',   label: 'ELSE' },
];